/**
 * RIFT — main menu / front-end.
 *
 * A self-contained ES module. One class, its own scoped `.rm-` stylesheet, no
 * external assets (every pixel is CSS / SVG / canvas or the live engine behind
 * it). Construct as:
 *
 *     const menu = new MainMenu(hostEl, ctx, { onPlay });
 *     menu.show();
 *
 * `onPlay()` fires from a real click on PLAY, so it is a valid pointer-lock
 * gesture. The module is defensive by contract: the operator hero ALWAYS renders
 * from a hand-authored SVG, and PLAY always works, even if the optional live-3D
 * enhancement throws. Nothing here can black-screen the game.
 *
 * Creative direction: TACTICAL-HUD. A cold, systems-driven console — angled
 * panel shears, scanline grain, corner brackets, a squad readout, an isometric
 * deployment cube, telemetry readouts, a left nav rail with an animated
 * selection carat, and a rotating armillary PLAY dial centered on the stage.
 * The identity card reads like a service record; the operator file panel
 * carries tabbed BIO/KIT/HISTORY detail over a photo grid. Motion is precise
 * and mechanical.
 */

import { trace } from '../core/trace.js';
import { accountLogin, accountLogout, accountMe, accountRegister, beginOAuth } from '../auth.js';
import { MapVote } from './mapvote.js';
import { Armory } from './armory.js';
import { Personnel } from './personnel.js';
import { Leaderboard } from './leaderboard.js';
import { Settings } from './settings.js';
import { listMaps, rememberMapId, resolveMapId, getMap } from '../world/maps/index.js';
import { OPERATORS } from './operators.js';
import { OPERATOR_SVG, RANK_SVG } from './art.js';

const STYLE_ID = 'rm-mainmenu-style';

/**
 * Where the deployment choice is parked across the reload that loads a new map.
 * The world is built once, at boot, from a single map — so voting for a map the
 * engine is not currently holding means booting again. Rather than dumping the
 * player back at the home screen having lost their playlist, the choice is
 * remembered and the flow resumes on the far side at the DEPLOY step.
 *
 * sessionStorage, not localStorage: it belongs to this tab and this sitting.
 */
const INTENT_KEY = 'rift:deploy-intent';

/* The secondary navigation. `id` is resolved in _activate(). */
const NAV = [
  { id: 'play', label: 'Play', sub: 'CHOOSE OPERATION', primary: true },
  { id: 'loadout', label: 'Shop', sub: 'WEAPONS · LOADOUT' },
  { id: 'operators', label: 'Operators', sub: 'SELECT UNIT' },
  { id: 'leaderboard', label: 'Leaderboard', sub: 'GLOBAL RANKINGS' },
  { id: 'settings', label: 'Settings', sub: 'SYSTEM CONFIG' },
  { id: 'quit', label: 'Quit', sub: 'END SESSION' },
];

const TICKER = [
  'SEASON 01 // "FRACTURE" LIVE',
  'NEW CONTRACT: SIGNAL BLACKOUT — 2X XP',
  'PATCH 0.9.4 — RECOIL PASS, NETCODE',
  'RANKED PLACEMENTS OPEN',
  'OPERATOR: BREACHER UNLOCKED AT LVL 12',
];

export class MainMenu {
  /**
   * @param {HTMLElement} host   where to mount (a div is created inside it)
   * @param {object}      ctx    the engine context (all reads are optional)
   * @param {{onPlay?:Function}} opts
   */
  constructor(host, ctx, opts = {}) {
    this.host = host || document.body;
    this.ctx = ctx || null;
    this.onPlay = opts.onPlay || null;
    // A map change reloads the page and restores the final GO screen before
    // UiSystem has finished wiring its match-start callback. Keep a click made
    // during that short boot window instead of making the player guess when it
    // is safe to press GO again.
    this._pendingPlay = null;
    this._hasPendingPlay = false;
    // Same boot-window race as onPlay above, but for the online matchmaking
    // handoff: a GO click during that window must not be dropped on the floor.
    this._pendingQueue = null;
    this.onQueue = opts.onQueue || null;
    this.onQueueCancel = opts.onQueueCancel || null;

    this.visible = false;
    this.selected = 0;
    this.opIndex = 0;
    this.flowPage = 'home';
    this.sessionType = null;
    this.matchMode = null;
    this.account = null;
    this.providers = { google: false, discord: false };
    this.accountMode = 'login';
    this._raf = 0;
    this._t = 0;
    this._three = null; // live-3D state, if it ever comes up
    this._disposed = false;
    // ---- online matchmaking handoff ---------------------------------------
    this._onlineQueueActive = false;
    this._queueStop = null;
    // ---- online players / duel challenges --------------------------------
    /** id of whoever we last sent a challenge to, while we wait for a reply. */
    this._pendingChallengeTarget = null;
    /** `{ fromId, fromName }` while the incoming-challenge modal is open. */
    this._incomingChallenge = null;

    try {
      this._injectStyle();
      this._build();
      this._wire();
      this._wireLobbyEvents();
    } catch (err) {
      // Even construction is guarded: a broken menu must never take the app
      // down. Fall back to a bare, working PLAY button.
      console.warn('[mainmenu] build failed, using minimal fallback', err);
      this._buildFallback();
    }
  }

  /* ==================================================================== */
  /* style                                                                */
  /* ==================================================================== */

  _injectStyle() {
    if (document.getElementById(STYLE_ID)) return;
    const s = document.createElement('style');
    s.id = STYLE_ID;
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  /* ==================================================================== */
  /* dom                                                                  */
  /* ==================================================================== */

  _el(tag, cls, parent, text) {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    if (parent) parent.appendChild(n);
    return n;
  }

  _build() {
    const identity = this._identity();

    this.root = this._el('div', 'rm-root', null);
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-label', 'RIFT main menu');
    this.root.style.display = 'none';

    /* ---- living background layers ---------------------------------- */
    const bg = this._el('div', 'rm-bg', this.root);
    this._el('div', 'rm-bg-grad', bg);
    const haze = this._el('div', 'rm-bg-haze', bg);
    this._el('i', null, haze);
    this._el('i', null, haze);
    this._el('i', null, haze);
    // diagonal energy beam sweep
    this._el('div', 'rm-bg-beam', bg);
    // drifting motes
    const motes = this._el('div', 'rm-motes', bg);
    for (let i = 0; i < 18; i++) {
      const m = this._el('i', null, motes);
      m.style.setProperty('--x', (5 + (i * 5.3) % 92).toFixed(2) + '%');
      m.style.setProperty('--d', (8 + (i % 5) * 3.2).toFixed(1) + 's');
      m.style.setProperty('--delay', (-(i * 1.4)).toFixed(1) + 's');
      m.style.setProperty('--s', (1 + (i % 3) * 0.7).toFixed(2));
    }
    this._el('div', 'rm-grid', bg);
    this._el('div', 'rm-scan', bg);
    this._el('div', 'rm-grain', bg);
    this._el('div', 'rm-vignette', bg);

    /* ---- global corner brackets ------------------------------------ */
    const frame = this._el('div', 'rm-frame', this.root);
    for (const c of ['tl', 'tr', 'bl', 'br']) this._el('div', 'rm-brk rm-brk-' + c, frame);

    /* ---- stage grid ------------------------------------------------- */
    const stage = this._el('div', 'rm-stage', this.root);
    this.stage = stage;

    /* ---- top service-record + telemetry ---------------------------- */
    const top = this._el('div', 'rm-top rm-rise', stage);
    top.style.setProperty('--rise', '0.02s');
    this._serviceRecord(top, identity);
    this._telemetry(top);
    this._squadTop(top);

    /* ---- left: title + nav rail ------------------------------------ */
    const left = this._el('div', 'rm-left', stage);

    const brand = this._el('div', 'rm-brand rm-rise', left);
    brand.style.setProperty('--rise', '0.08s');
    const logo = this._el('div', 'rm-logo', brand);
    this._el('span', 'rm-logo-main', logo, 'RIFT');
    this._el('span', 'rm-logo-tag', logo, '//');
    this._el('div', 'rm-tagline', brand, 'TACTICAL OPERATIONS — BREACH THE LINE');
    const meta = this._el('div', 'rm-brand-meta', brand);
    this._el('span', null, meta, 'CLASSIFIED');
    this._el('span', 'rm-dot', meta);
    this._el('span', null, meta, 'THEATRE: FRACTURE');

    const nav = this._el('nav', 'rm-nav rm-rise', left);
    nav.style.setProperty('--rise', '0.14s');
    nav.setAttribute('aria-label', 'Main navigation');
    this.navItems = [];
    NAV.forEach((item, i) => {
      const b = this._el('button', 'rm-navitem' + (item.primary ? ' rm-primary' : ''), nav);
      b.type = 'button';
      b.dataset.id = item.id;
      b.style.setProperty('--i', String(i));
      this._el('span', 'rm-carat', b);
      const body = this._el('span', 'rm-nav-body', b);
      this._el('span', 'rm-nav-label', body, item.label);
      this._el('span', 'rm-nav-sub', body, item.sub);
      this._el('span', 'rm-nav-idx', b, String(i + 1).padStart(2, '0'));
      b.addEventListener('mouseenter', () => this._select(i, false));
      b.addEventListener('focus', () => this._select(i, false));
      b.addEventListener('click', () => this._activate(i));
      this.navItems.push(b);
    });

    const hint = this._el('div', 'rm-hint rm-rise', left);
    hint.style.setProperty('--rise', '0.2s');
    this._key(hint, 'W');
    this._key(hint, 'S');
    this._el('span', 'rm-hint-t', hint, 'NAVIGATE');
    this._key(hint, '↵');
    this._el('span', 'rm-hint-t', hint, 'SELECT');

    /* ---- center: rotating armillary PLAY dial ----------------------- */
    const heroWrap = this._el('div', 'rm-hero', stage);
    const dial = this._el('div', 'rm-dial rm-rise', heroWrap);
    dial.style.setProperty('--rise', '0.1s');
    // outer backing ring + slow-rotating outer arc that frames the dial.
    // Sits behind the dial SVG, reads as an "instrument bezel".
    const bezel = this._el('div', 'rm-dial-bezel', heroWrap);
    bezel.innerHTML = BEZEL_SVG;
    dial.innerHTML = DIAL_SVG;
    this.dialPlay = this._el('button', 'rm-dial-play', dial);
    this.dialPlay.type = 'button';
    this.dialPlay.setAttribute('aria-label', 'Play — choose operation');
    this._el('span', 'rm-dial-play-eyebrow', this.dialPlay, '// READY');
    this._el('span', 'rm-dial-play-label', this.dialPlay, 'PLAY');
    this.dialPlay.addEventListener('click', () => this._showSession());
    // four "corner bolts" — they make the dial look mounted, not floating
    for (const c of ['tl', 'tr', 'bl', 'br']) this._el('i', 'rm-dial-bolt rm-dial-bolt-' + c, dial);

    /* ---- right: deployment cube + war room + operator file ----------- */
    const right = this._el('div', 'rm-right', stage);

    const deploy = this._el('div', 'rm-panel rm-deploy rm-rise', right);
    deploy.style.setProperty('--rise', '0.18s');
    this._panelHead(deploy, 'DEPLOYMENT', 'SECTOR 7 · GRID K-19');
    const map = this._el('div', 'rm-map', deploy);
    map.innerHTML = CUBE_SVG;
    this.mapPing = map.querySelector('.rm-map-ping');
    const legend = this._el('div', 'rm-map-legend', deploy);
    this._legend(legend, 'rm-lg-us', 'FRIENDLY 06');
    this._legend(legend, 'rm-lg-them', 'HOSTILE 09');
    this._legend(legend, 'rm-lg-obj', 'OBJECTIVE');

    /* war room — live intel between deployment and operator file */
    const war = this._el('div', 'rm-panel rm-war rm-rise', right);
    war.style.setProperty('--rise', '0.21s');
    this._panelHead(war, 'WAR ROOM', 'LIVE');
    const warRows = this._el('div', 'rm-war-rows', war);
    this._warRow(warRows, 'THREAT', 'ELEVATED', 'amber');
    this._warRow(warRows, 'SERVERS', '14 / 14', 'ok');
    this._warRow(warRows, 'OPERATORS ONLINE', '2,841', '');
    this._warRow(warRows, 'NEXT OP', '04 : 27', 'cyan');
    const warBar = this._el('div', 'rm-war-bar', war);
    this._el('span', 'rm-war-bar-k', warBar, 'SECTOR CONTROL');
    const warBarTrack = this._el('div', 'hud-bar rm-war-bar-track', warBar);
    this._el('i', null, warBarTrack);
    this._el('span', 'rm-war-bar-v', warBar, '67%');
    /* tiny live ticker of fictional ops */
    const warLive = this._el('div', 'rm-war-live', war);
    this._el('span', 'rm-war-live-dot', warLive);
    this._el('span', 'rm-war-live-tag', warLive, 'LIVE FEED');
    this.warLiveText = this._el('span', 'rm-war-live-text', warLive, 'SECTOR 7 — GRID K-19 UNDER OBSERVATION');
    this._warIdx = 0;
    this._warLines = [
      'SECTOR 7 — GRID K-19 UNDER OBSERVATION',
      'CONVOY ECHO MOVING — STANDING BY',
      'CONTRACT EXPIRED — NEUTRALIZED',
      'NEW UPLINK — STABLE · 24 MS',
      'INTEL UPDATED — ROE REVISED',
    ];

    const readout = this._el('div', 'rm-panel rm-readout rm-rise', right);
    readout.style.setProperty('--rise', '0.24s');
    const readoutHead = this._panelHead(readout, 'OPERATOR FILE', OPERATORS[this.opIndex].name);
    this.opSwitch = this._el('div', 'rm-op-switch', readoutHead);
    const opPrev = this._el('button', 'rm-op-arrow', this.opSwitch, '‹');
    opPrev.type = 'button';
    opPrev.setAttribute('aria-label', 'Previous operator');
    opPrev.addEventListener('click', () => this._selectOperator((this.opIndex - 1 + OPERATORS.length) % OPERATORS.length));
    const opNext = this._el('button', 'rm-op-arrow', this.opSwitch, '›');
    opNext.type = 'button';
    opNext.setAttribute('aria-label', 'Next operator');
    opNext.addEventListener('click', () => this._selectOperator((this.opIndex + 1) % OPERATORS.length));

    const media = this._el('div', 'rm-op-media', readout);
    this.opPortrait = this._el('div', 'rm-op-portrait', media);
    this.heroStage = this.opPortrait;
    const portraitArt = this._el('div', 'rm-op-portrait-art', this.opPortrait);
    portraitArt.innerHTML = OPERATOR_SVG;
    this.heroSvgWrap = portraitArt;
    // scan-line overlay that sweeps on operator switch
    this._el('i', 'rm-op-scan', this.opPortrait);
    this._el('i', 'rm-op-crosshair', this.opPortrait);
    // optional live-3D canvas mounts here, over the guaranteed SVG
    this.heroCanvasWrap = this._el('div', 'rm-op-portrait-canvas', this.opPortrait);
    const mediaSide = this._el('div', 'rm-op-media-side', media);
    const swatchKit = this._el('div', 'rm-op-swatch rm-op-swatch-a', mediaSide);
    this._el('span', 'rm-op-swatch-label', swatchKit, 'KIT');
    const swatchField = this._el('div', 'rm-op-swatch rm-op-swatch-b', mediaSide);
    this._el('span', 'rm-op-swatch-label', swatchField, 'FIELD');

    const opTabs = this._el('div', 'rm-op-tabs', readout);
    this.opTabButtons = [];
    for (const tab of ['bio', 'kit', 'history']) {
      const b = this._el('button', 'rm-op-tab', opTabs, tab.toUpperCase());
      b.type = 'button';
      b.dataset.tab = tab;
      b.addEventListener('click', () => this._setOpTab(tab));
      this.opTabButtons.push(b);
    }
    this.opTab = 'bio';
    this.fileRows = this._el('div', 'rm-file', readout);
    this._refreshFile();

    /* ---- bottom status / utility bar ------------------------------- */
    const bottom = this._el('div', 'rm-bottom rm-rise', stage);
    bottom.style.setProperty('--rise', '0.3s');

    const build = this._el('div', 'rm-status-cell', bottom);
    this._el('span', 'rm-tag', build, 'BUILD');
    this._el('span', 'rm-mono', build, 'RIFT 0.9.4 · GLES3 · ' + (this._quality() || 'ULTRA'));

    const curr = this._el('div', 'rm-status-cell', bottom);
    this._el('span', 'rm-tag', curr, 'CREDITS');
    this._el('span', 'rm-mono rm-amber', curr, '◈ 12,450');

    const news = this._el('div', 'rm-status-cell rm-news', bottom);
    this._el('span', 'rm-tag', news, 'INTEL');
    const track = this._el('div', 'rm-ticker', news);
    const line = this._el('div', 'rm-ticker-line', track);
    // duplicate the run so the marquee loops seamlessly (translateX -50%)
    for (let r = 0; r < 2; r++) {
      for (const t of TICKER) {
        this._el('span', 'rm-ticker-i', line, t);
        this._el('span', 'rm-ticker-sep', line, '◦');
      }
    }

    this.host.appendChild(this.root);
    this._buildPlayFlow();
    this._buildAccountDialog();
    this._buildChallengeDialog();
    this._buildSpecialPopup();
    this._select(0, false);
    this._selectOperator(0);
    this._loadAccount();
  }

  /**
   * The deployment flow deliberately lives over the home screen instead of
   * replacing it. It keeps the selected operator in view while the player
   * makes the only two choices needed to start a match: opponent type, then
   * playlist. All labels are original to RIFT; no game branding is borrowed.
   */
  _buildPlayFlow() {
    const flow = this._el('section', 'rm-flow', this.root);
    flow.setAttribute('role', 'dialog');
    flow.setAttribute('aria-modal', 'true');
    flow.setAttribute('aria-live', 'polite');
    flow.setAttribute('aria-label', 'Deployment setup');
    flow.setAttribute('aria-hidden', 'true');
    this.flow = flow;
    this.flowEyebrow = this._el('div', 'rm-flow-eyebrow', flow, 'OPERATION SETUP');
    this.flowTitle = this._el('h1', 'rm-flow-title', flow, 'HOW DO YOU WANT TO DEPLOY?');
    this.flowCopy = this._el('p', 'rm-flow-copy', flow, 'Select a combat environment. You can change your operator at any time.');
    this.flowChoices = this._el('div', 'rm-flow-choices', flow);
    const footer = this._el('div', 'rm-flow-footer', flow);
    this.flowBack = this._el('button', 'rm-flow-back', footer, 'Back');
    this.flowBack.type = 'button';
    this.flowBack.addEventListener('click', () => this._flowBack());
    this.flowStep = this._el('span', 'rm-flow-step', footer, 'STEP 01 / 02');
  }

  _buildAccountDialog() {
    const modal = this._el('section', 'rm-account-modal', this.root);
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', 'Rift account');
    modal.hidden = true;
    this.accountModal = modal;

    const card = this._el('div', 'rm-account-card', modal);
    const head = this._el('div', 'rm-account-head', card);
    const title = this._el('div', null, head);
    this._el('span', 'rm-account-kicker', title, 'OPERATOR IDENTITY');
    this.accountTitle = this._el('h2', 'rm-account-title', title, 'SIGN IN');
    const close = this._el('button', 'rm-account-close', head, 'Close');
    close.type = 'button';
    close.addEventListener('click', () => this._closeAccount());

    this.accountSignedIn = this._el('div', 'rm-account-signedin', card);
    this.accountSignedIn.hidden = true;
    this.accountSummary = this._el('p', 'rm-account-copy', this.accountSignedIn);
    const out = this._el('button', 'rm-account-submit', this.accountSignedIn, 'SIGN OUT');
    out.type = 'button';
    out.addEventListener('click', () => this._logoutAccount());

    this.accountFormArea = this._el('div', 'rm-account-form-area', card);
    const tabs = this._el('div', 'rm-account-tabs', this.accountFormArea);
    this.accountLoginTab = this._el('button', 'rm-account-tab', tabs, 'SIGN IN');
    this.accountRegisterTab = this._el('button', 'rm-account-tab', tabs, 'CREATE ACCOUNT');
    for (const [button, mode] of [[this.accountLoginTab, 'login'], [this.accountRegisterTab, 'register']]) {
      button.type = 'button';
      button.addEventListener('click', () => this._setAccountMode(mode));
    }
    const form = this._el('form', 'rm-account-form', this.accountFormArea);
    this.accountForm = form;
    const nameLabel = this._el('label', 'rm-account-label', form, 'CALLSIGN');
    this.accountName = this._el('input', 'rm-account-input', nameLabel);
    this.accountName.name = 'username';
    this.accountName.autocomplete = 'username';
    this.accountName.maxLength = 16;
    this.accountName.required = true;
    this.accountName.pattern = '[A-Za-z0-9_-]{3,16}';
    this.accountName.placeholder = 'YOUR-CALLSIGN';
    const passLabel = this._el('label', 'rm-account-label', form, 'PASSWORD');
    this.accountPassword = this._el('input', 'rm-account-input', passLabel);
    this.accountPassword.name = 'password';
    this.accountPassword.type = 'password';
    this.accountPassword.autocomplete = 'current-password';
    this.accountPassword.minLength = 8;
    this.accountPassword.maxLength = 128;
    this.accountPassword.required = true;
    this.accountPassword.placeholder = 'AT LEAST 8 CHARACTERS';
    this.accountSubmit = this._el('button', 'rm-account-submit', form, 'SIGN IN');
    this.accountSubmit.type = 'submit';
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      this._submitAccount();
    });

    this.accountDivider = this._el('div', 'rm-account-divider', this.accountFormArea, 'OR CONTINUE WITH');
    const providers = this._el('div', 'rm-account-providers', this.accountFormArea);
    this.googleButton = this._el('button', 'rm-account-provider rm-google', providers, 'GOOGLE');
    this.discordButton = this._el('button', 'rm-account-provider rm-discord', providers, 'DISCORD');
    this.googleButton.type = this.discordButton.type = 'button';
    this.googleButton.addEventListener('click', () => beginOAuth('google'));
    this.discordButton.addEventListener('click', () => beginOAuth('discord'));
    this.accountMessage = this._el('p', 'rm-account-message', card, '');
    this._renderAccount();
  }

  /** Same fixed-node-toggled-by-`hidden` shape as the account dialog above —
   *  built once, repopulated each time an incoming duel challenge arrives. */
  _buildChallengeDialog() {
    const modal = this._el('section', 'rm-account-modal rm-challenge-modal', this.root);
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', 'Duel challenge');
    modal.hidden = true;
    this.challengeModal = modal;

    const card = this._el('div', 'rm-account-card rm-challenge-card', modal);
    const head = this._el('div', 'rm-account-head', card);
    const title = this._el('div', null, head);
    this._el('span', 'rm-account-kicker', title, 'DUEL CHALLENGE');
    this.challengeTitle = this._el('h2', 'rm-account-title rm-challenge-title', title, '');
    const close = this._el('button', 'rm-account-close', head, 'Close');
    close.type = 'button';
    close.addEventListener('click', () => this._declineChallenge());

    this._el('p', 'rm-account-copy', card, 'Pick the map — the duel runs on your choice.');
    this.challengeMapRow = this._el('div', 'rm-challenge-maps', card);

    const actions = this._el('div', 'rm-challenge-actions', card);
    this.challengeAcceptButton = this._el('button', 'rm-account-submit rm-challenge-accept', actions, 'ACCEPT');
    this.challengeAcceptButton.type = 'button';
    this.challengeAcceptButton.addEventListener('click', () => this._acceptChallenge());
    this.challengeDeclineButton = this._el('button', 'rm-account-provider rm-challenge-decline', actions, 'DECLINE');
    this.challengeDeclineButton.type = 'button';
    this.challengeDeclineButton.addEventListener('click', () => this._declineChallenge());
  }

  async _loadAccount() {
    try {
      const data = await accountMe();
      this.account = data.account ?? null;
      this.providers = data.providers ?? this.providers;
    } catch {
      // Local/offline previews intentionally have no auth service. The rest of
      // the menu remains fully playable in that state.
    }
    this._renderAccount();
    this.ctx?.peek?.('player')?.refreshProgression?.();
    const query = new URLSearchParams(location.search);
    if (query.get('auth') === 'error') {
      this._openAccount('Sign-in did not complete. Please try again.');
    }
    const special = query.get('special');
    if (special) {
      this._openSpecialPopup(special);
      query.delete('special');
      const qs = query.toString();
      history.replaceState({}, '', `${location.pathname}${qs ? `?${qs}` : ''}${location.hash}`);
    }
  }

  _buildSpecialPopup() {
    const modal = this._el('section', 'rm-special-modal', this.root);
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', 'Message');
    modal.hidden = true;
    this.specialModal = modal;

    const card = this._el('div', 'rm-special-card', modal);
    this.specialText = this._el('p', 'rm-special-copy', card, '');
    const close = this._el('button', 'rm-special-close', card, 'CLOSE');
    close.type = 'button';
    close.addEventListener('click', () => this._closeSpecialPopup());
  }

  _openSpecialPopup(text) {
    if (!this.specialModal) return;
    this.specialText.textContent = text;
    this.specialModal.hidden = false;
    this.specialModal.setAttribute('aria-hidden', 'false');
  }

  _closeSpecialPopup() {
    if (!this.specialModal) return;
    this.specialModal.hidden = true;
    this.specialModal.setAttribute('aria-hidden', 'true');
  }

  _renderAccount() {
    const signedIn = !!this.account;
    if (this.callsignEl && signedIn) this.callsignEl.textContent = this.account.username;
    this._refreshProgression();
    if (this.accountButton) this.accountButton.textContent = signedIn ? 'ACCOUNT' : 'CREATE / SIGN IN';
    if (!this.accountModal) return;
    this.accountSignedIn.hidden = !signedIn;
    this.accountFormArea.hidden = signedIn;
    if (signedIn) {
      this.accountSummary.textContent = `${this.account.username} is secured with ${this.account.method}. Your callsign is used in multiplayer.`;
    } else {
      this.accountTitle.textContent = this.accountMode === 'register' ? 'CREATE ACCOUNT' : 'SIGN IN';
      this.accountSubmit.textContent = this.accountMode === 'register' ? 'CREATE ACCOUNT' : 'SIGN IN';
      this.accountPassword.autocomplete = this.accountMode === 'register' ? 'new-password' : 'current-password';
      this.accountLoginTab.classList.toggle('rm-on', this.accountMode === 'login');
      this.accountRegisterTab.classList.toggle('rm-on', this.accountMode === 'register');
      this.googleButton.disabled = !this.providers.google;
      this.discordButton.disabled = !this.providers.discord;
      this.googleButton.title = this.providers.google ? '' : 'Google sign-in is being configured.';
      this.discordButton.title = this.providers.discord ? '' : 'Discord sign-in is being configured.';
    }
  }

  _openAccount(message = '') {
    if (!this.accountModal) return;
    this.accountModal.hidden = false;
    this.accountModal.style.display = '';
    this.accountModal.setAttribute('aria-hidden', 'false');
    this.root.classList.add('rm-account-open');
    this.accountMessage.textContent = message;
    this._renderAccount();
    setTimeout(() => (this.account ? this.accountSignedIn.querySelector('button') : this.accountName)?.focus({ preventScroll: true }), 20);
  }

  _closeAccount() {
    if (!this.accountModal) return;
    this.accountModal.hidden = true;
    this.accountModal.setAttribute('aria-hidden', 'true');
    this.root.classList.remove('rm-account-open');
    this.accountMessage.textContent = '';
  }

  /* ==================================================================== */
  /* online players / duel challenges                                    */
  /* ==================================================================== */

  /** Live updates pushed from `lobby` (`src/net/lobby.js`) via `ctx.events` —
   *  the same "peek the system directly, register for its broadcasts"
   *  pattern `net.onMapVote` already uses, just over the shared event bus
   *  instead of a bespoke handler object. */
  _wireLobbyEvents() {
    if (!this.ctx?.events) return;
    this._offProgression = this.ctx.events.on('progression:changed', () => this._refreshProgression());
    this._offLobbyRoster = this.ctx.events.on('lobby:roster', () => this._paintOnlinePlayers());
    this._offLobbyChallenge = this.ctx.events.on('lobby:challenge', (e) => {
      this._showIncomingChallenge(e.fromId, e.fromName);
    });
    this._offLobbyChallengeSent = this.ctx.events.on('lobby:challenge-sent', (e) => {
      this._pendingChallengeTarget = e.targetId;
      this._paintOnlinePlayers();
    });
    this._offLobbyChallengeDeclined = this.ctx.events.on('lobby:challenge-declined', (e) => {
      if (this._pendingChallengeTarget === e.targetId) this._pendingChallengeTarget = null;
      this._flash('CHALLENGE DECLINED');
      this._paintOnlinePlayers();
    });
    this._offLobbyChallengeExpired = this.ctx.events.on('lobby:challenge-expired', (e) => {
      if (this._pendingChallengeTarget === e.targetId) this._pendingChallengeTarget = null;
      this._flash('CHALLENGE EXPIRED — NO RESPONSE');
      this._paintOnlinePlayers();
    });
  }

  /** Built once, into whichever container is currently showing the mode
   *  cards (`_renderFlow`'s online `modes` branch) — repainted in place on
   *  every `lobby:roster` broadcast rather than re-running the whole flow. */
  _renderOnlinePlayers(container) {
    const panel = this._el('div', 'rm-online-panel', container);
    this._el('div', 'rm-online-heading', panel, 'ONLINE PLAYERS');
    this._onlinePlayersList = this._el('div', 'rm-online-list', panel);
    this._paintOnlinePlayers();
  }

  _paintOnlinePlayers() {
    const list = this._onlinePlayersList;
    if (!list || !list.isConnected) return;
    list.textContent = '';
    const lobby = this.ctx?.peek?.('lobby');
    const players = (lobby?.roster ?? []).filter((p) => p.id !== lobby?.id);
    if (!players.length) {
      this._el('div', 'rm-online-empty', list, 'No one else is online right now.');
      return;
    }
    for (const p of players) {
      const row = this._el('div', 'rm-online-row', list);
      this._el('span', 'rm-online-name', row, p.name);
      const waiting = this._pendingChallengeTarget === p.id;
      const btn = this._el('button', 'rm-online-duel', row, waiting ? 'WAITING…' : 'DUEL');
      btn.type = 'button';
      btn.disabled = waiting;
      btn.addEventListener('click', () => {
        try { this.ctx?.peek?.('lobby')?.challenge(p.id); } catch (err) { console.warn('[mainmenu] challenge failed', err); }
      });
    }
  }

  _showIncomingChallenge(fromId, fromName) {
    if (!this.challengeModal) return;
    this._incomingChallenge = { fromId, fromName };
    this.challengeTitle.textContent = `${fromName} HAS CHALLENGED YOU TO A DUEL`;

    const maps = listMaps();
    this.challengeMapRow.textContent = '';
    let chosen = maps[0]?.id ?? null;
    const paintMaps = () => {
      for (const btn of this.challengeMapRow.children) {
        btn.classList.toggle('rm-challenge-map-active', btn.dataset.mapId === chosen);
      }
    };
    for (const m of maps) {
      const btn = this._el('button', 'rm-challenge-map-btn', this.challengeMapRow, m.name ?? m.id);
      btn.type = 'button';
      btn.dataset.mapId = m.id;
      btn.addEventListener('click', () => {
        chosen = m.id;
        paintMaps();
      });
    }
    this.challengeAcceptButton.onclick = () => {
      try { this.ctx?.peek?.('lobby')?.acceptChallenge(fromId, chosen); } catch (err) { console.warn('[mainmenu] accept challenge failed', err); }
      this._closeChallengeModal();
    };
    paintMaps();

    this.challengeModal.hidden = false;
    this.challengeModal.setAttribute('aria-hidden', 'false');
    // A separate class from the account modal's — a challenge can arrive
    // while that modal (or nothing at all) is already open, and each must be
    // able to close without un-dimming the stage out from under the other.
    this.root.classList.add('rm-challenge-open');
    setTimeout(() => this.challengeAcceptButton?.focus({ preventScroll: true }), 20);
  }

  _declineChallenge() {
    if (this._incomingChallenge) {
      try { this.ctx?.peek?.('lobby')?.declineChallenge(this._incomingChallenge.fromId); } catch (err) { console.warn('[mainmenu] decline challenge failed', err); }
    }
    this._closeChallengeModal();
  }

  _closeChallengeModal() {
    if (!this.challengeModal) return;
    this.challengeModal.hidden = true;
    this.challengeModal.setAttribute('aria-hidden', 'true');
    this.root.classList.remove('rm-challenge-open');
    this._incomingChallenge = null;
  }

  _setAccountMode(mode) {
    this.accountMode = mode;
    this.accountMessage.textContent = '';
    this._renderAccount();
    this.accountPassword.focus({ preventScroll: true });
  }

  async _submitAccount() {
    const username = this.accountName.value.trim();
    const password = this.accountPassword.value;
    this.accountSubmit.disabled = true;
    this.accountMessage.textContent = 'SECURING OPERATOR IDENTITY…';
    try {
      const data = this.accountMode === 'register'
        ? await accountRegister(username, password)
        : await accountLogin(username, password);
      this.account = data.account;
      localStorage.setItem('cod:name', this.account.username.toUpperCase().slice(0, 16));
      await this.ctx?.peek?.('player')?.refreshProgression?.();
      this.accountPassword.value = '';
      this.accountMessage.textContent = 'SIGNED IN.';
      this._renderAccount();
      setTimeout(() => this._closeAccount(), 600);
    } catch (err) {
      this.accountMessage.textContent = err?.message || 'Account service is unavailable.';
    } finally {
      this.accountSubmit.disabled = false;
    }
  }

  async _logoutAccount() {
    this.accountMessage.textContent = 'SIGNING OUT…';
    try {
      await accountLogout();
      this.account = null;
      await this.ctx?.peek?.('player')?.refreshProgression?.();
      this.accountMessage.textContent = 'SIGNED OUT.';
      this._renderAccount();
    } catch (err) {
      this.accountMessage.textContent = err?.message || 'Could not sign out.';
    }
  }

  _showSession() {
    this.flowPage = 'session';
    this._setFlowOpen(true);
    this._renderFlow();
  }

  _showModes(type) {
    this.sessionType = type;
    this.flowPage = 'modes';
    // Online players can only be listed/challenged once we are actually
    // holding a lobby connection — see `src/net/lobby.js`. Offline has no
    // such list, so leaving (or never having) the connection is correct too.
    try {
      if (type === 'online') this.ctx?.peek?.('lobby')?.connect();
      else this.ctx?.peek?.('lobby')?.disconnect();
    } catch (err) {
      console.warn('[mainmenu] lobby connect failed', err);
    }
    this._renderFlow();
  }

  _flowBack() {
    if (this.flowPage === 'queue') {
      this._cancelOnlineQueue();
      this.flowPage = 'modes';
      this._renderFlow();
      return;
    }
    if (this.flowPage === 'modes' || this.flowPage === 'ready') {
      this.flowPage = 'session';
      try { this.ctx?.peek?.('lobby')?.disconnect(); } catch { /* best effort */ }
      this._renderFlow();
      return;
    }
    this.flowPage = 'home';
    this._setFlowOpen(false);
    this.navItems[0]?.focus({ preventScroll: true });
  }

  _setFlowOpen(open) {
    this.root.classList.toggle('rm-flow-open', open);
    this.flow?.classList.toggle('rm-flow-show', open);
    this.flow?.setAttribute('aria-hidden', String(!open));
    // `inert` prevents both pointer and tab focus from leaking into the muted
    // home screen beneath this modal. The CSS rule remains a visual fallback.
    if (this.stage) this.stage.inert = !!open;
  }

  _renderFlow() {
    if (!this.flow) return;
    this._setFlowOpen(true);
    this.flowChoices.textContent = '';
    this.flowButtons = [];

    if (this.flowPage === 'queue') {
      this.flowEyebrow.textContent = 'MULTIPLAYER / LIVE QUEUE';
      this.flowTitle.textContent = 'SEARCHING FOR REAL OPERATORS';
      this.flowCopy.textContent = 'RIFT will only deploy when the required number of real players is connected. No bots are substituted for online matches.';
      this.flowStep.textContent = 'QUEUE ACTIVE';
      this.flowBack.textContent = 'Cancel queue';
      const readout = this._el('div', 'rm-queue-readout', this.flowChoices);
      this.queueStatus = this._el('span', 'rm-queue-status', readout, 'CONNECTING');
      this.queueCount = this._el('strong', 'rm-queue-count', readout, '0 / 0 PLAYERS');
      this.queueDetail = this._el('span', 'rm-queue-detail', readout, 'CONTACTING MULTIPLAYER SERVICE');
      this._setQueueStatus(this.queueState);
    } else if (this.flowPage === 'ready') {
      // Landed here after a reload that built the voted map. One click left, and
      // it doubles as the pointer-lock gesture the browser requires.
      const map = getMap(this._loadedMapId());
      this.flowEyebrow.textContent = 'OPERATION SETUP / READY';
      this.flowTitle.textContent = 'MAP LOADED';
      this.flowCopy.textContent = map
        ? `${map.theme}. ${map.playstyle ?? ''}`.trim()
        : 'The voted map is loaded and ready.';
      this.flowStep.textContent = 'READY TO DEPLOY';
      this.flowBack.textContent = 'Change playlist';
      this._flowChoice({
        label: 'GO',
        eyebrow: (map?.name ?? 'MAP') + ' / ' + (this.matchMode ?? 'TDM'),
        copy: 'Deploy now on the map the lobby voted for.',
        meta: this.sessionType === 'online' ? 'ONLINE / REAL PLAYERS' : 'OFFLINE / AI OPPONENTS',
        accent: this.sessionType === 'online' ? 'cyan' : 'amber',
        action: () => this._startMatch(this.matchMode ?? 'TDM', this._loadedMapId()),
      });
    } else if (this.flowPage === 'session') {
      this.flowEyebrow.textContent = 'OPERATION SETUP / 01';
      this.flowTitle.textContent = 'CHOOSE YOUR COMBAT ENVIRONMENT';
      this.flowCopy.textContent = 'Online matches connect you only with other players. Offline matches build a private combat drill with AI opponents.';
      this.flowStep.textContent = 'STEP 01 / 02';
      this.flowBack.textContent = 'Return to home';
      this._flowChoice({
        label: 'Online',
        eyebrow: 'MULTIPLAYER',
        copy: 'Real-player matches over the RIFT multiplayer service.',
        meta: 'LIVE ROSTER / NETWORK',
        accent: 'cyan',
        action: () => this._showModes('online'),
      });
      this._flowChoice({
        label: 'Offline',
        eyebrow: 'COMBAT DRILL',
        copy: 'Train locally against tactical AI. No network session required.',
        meta: 'LOCAL / AI OPPONENTS',
        accent: 'amber',
        action: () => this._showModes('offline'),
      });
    } else {
      const online = this.sessionType === 'online';
      this.flowEyebrow.textContent = `OPERATION SETUP / 02 / ${online ? 'ONLINE' : 'OFFLINE'}`;
      this.flowTitle.textContent = 'SELECT A PLAYLIST';
      this.flowCopy.textContent = online
        ? 'Choose a real-player playlist. Your match will deploy only after the required number of connected operators is verified.'
        : 'These local combat drills use the bot count shown on each card. Friendly-AI team rules are not simulated offline.';
      this.flowStep.textContent = 'STEP 02 / 02';
      this.flowBack.textContent = 'Change environment';
      const modes = online
        ? [
            ['1v1', 'DUEL', 'A focused head-to-head match against one real opponent.', '02 REAL PLAYERS'],
            ['2v2', 'FIRETEAM', 'Two-player squads face off in a real-player lobby.', '04 REAL PLAYERS'],
            ['TDM', 'TEAM DEATHMATCH', 'Score eliminations for your side against real players.', 'LIVE / REAL PLAYERS'],
            ['FFA', 'FREE FOR ALL', 'Every connected operator fights for the top score.', 'LIVE / REAL PLAYERS'],
          ]
        : [
            ['1v1', 'ONE-BOT DUEL', 'A local elimination drill against one AI opponent.', '01 AI OPPONENT'],
            ['2v2', 'FOUR-OPERATOR DRILL', 'A local combat drill with three AI opponents.', '03 AI OPPONENTS'],
            ['TDM', 'TEAM DEATH DRILL', 'An eight-bot local combat drill.', '08 AI OPPONENTS'],
            ['FFA', 'FREE-FOR-ALL DRILL', 'A five-bot local free-for-all drill.', '05 AI OPPONENTS'],
          ];
      for (const [id, name, copy, meta] of modes) {
        this._flowChoice({
          label: id,
          eyebrow: name,
          copy,
          meta,
          accent: online ? 'cyan' : 'amber',
          action: () => this._launchMatch(id),
        });
      }
      // Who else is here to duel, before a map is even picked — only
      // meaningful online, since offline drills have no other real players.
      if (online) this._renderOnlinePlayers(this.flowChoices);
    }

    const first = this.flowButtons[0] ?? this.flowBack;
    setTimeout(() => first?.focus({ preventScroll: true }), 60);
  }

  _setQueueStatus(state = {}) {
    this.queueState = state;
    if (!this.queueStatus) return;
    const status = state.status ?? 'CONNECTING';
    this.queueStatus.textContent = status;
    const joined = state.players ?? 0;
    const required = state.required ?? 0;
    this.queueCount.textContent = `${joined} / ${required} PLAYERS`;
    this.queueDetail.textContent = state.detail ?? 'CONTACTING MULTIPLAYER SERVICE';
    this.flow?.classList.toggle('rm-queue-error', status === 'UNAVAILABLE' || status === 'REJECTED');
  }

  _flowChoice({ label, eyebrow, copy, meta, accent, action }) {
    const b = this._el('button', `rm-flow-choice rm-flow-${accent}`, this.flowChoices);
    b.type = 'button';
    const lead = this._el('span', 'rm-flow-choice-lead', b);
    this._el('span', 'rm-flow-choice-label', lead, label);
    const text = this._el('span', 'rm-flow-choice-text', b);
    this._el('span', 'rm-flow-choice-eyebrow', text, eyebrow);
    this._el('span', 'rm-flow-choice-copy', text, copy);
    this._el('span', 'rm-flow-choice-meta', b, meta);
    this._el('span', 'rm-flow-choice-arrow', b, '→');
    b.addEventListener('click', action);
    this.flowButtons.push(b);
    return b;
  }

  /**
   * Playlist chosen — now the map. The vote sits here, between the playlist and
   * the deployment, because it is the last decision that can still change what
   * the engine has to load.
   */
  _launchMatch(mode) {
    this.matchMode = mode;
    this._openMapVote(mode);
  }

  /* ==================================================================== */
  /* armory                                                               */
  /* ==================================================================== */

  _openArmory() {
    this._setFlowOpen(false);
    this.armory?.dispose();
    this.armory = new Armory(this.host, {
      onClose: () => {
        this._flash('LOADOUT SAVED — ACTIVE NEXT DEPLOYMENT');
      },
    });
    this.armory.show();
  }

  /* ==================================================================== */
  /* personnel                                                            */
  /* ==================================================================== */

  _openPersonnel() {
    this._setFlowOpen(false);
    this.personnel?.dispose();
    this.personnel = new Personnel(this.host, {
      playerLevel: this._identity().level,
      activeId: OPERATORS[this.opIndex].id,
      onSelect: (id) => {
        const i = OPERATORS.findIndex((op) => op.id === id);
        if (i >= 0) this._selectOperator(i);
      },
      onClose: () => this._flash('OPERATOR FILE SYNCED'),
    });
    this.personnel.show();
  }

  /* ==================================================================== */
  /* leaderboard                                                          */
  /* ==================================================================== */

  _openLeaderboard() {
    this._setFlowOpen(false);
    this.leaderboard?.dispose();
    this.leaderboard = new Leaderboard(this.host, {
      playerName: this._identity().username,
    });
    this.leaderboard.show();
  }

  /* ==================================================================== */
  /* settings                                                             */
  /* ==================================================================== */

  _openSettings() {
    this._setFlowOpen(false);
    this.settings?.dispose();
    this.settings = new Settings(this.host, this.ctx, {
      onClose: () => this._flash('SYSTEM CONFIG SAVED'),
    });
    this.settings.show();
  }

  /* ==================================================================== */
  /* map vote                                                             */
  /* ==================================================================== */

  /** The map the engine is holding right now — a vote for this one costs nothing. */
  _loadedMapId() {
    try {
      return this.ctx?.peek?.('world')?.mapId ?? resolveMapId();
    } catch {
      return resolveMapId();
    }
  }

  _openMapVote(mode) {
    const maps = listMaps();
    // One map registered is not a vote. Skip straight to deploying rather than
    // showing a ballot with a single candidate.
    if (maps.length < 2) {
      this._startMatch(mode, this._loadedMapId());
      return;
    }

    const online = this.sessionType === 'online';

    this._setFlowOpen(false);
    this.mapVote?.dispose();
    // No live tally here any more: picking a map now means "matchmake me onto
    // this map", not "cast a vote in whatever match happens to be running"
    // (that older standing-vote system, `net.voteMap`/`net.onMapVote`, still
    // exists for the server's one persistent match — see `game.rs`'s
    // `vote_tally` — it is just not what this screen drives any more).
    this.mapVote = new MapVote(this.host, {
      maps,
      activeId: this._loadedMapId(),
      session: online ? 'online' : 'offline',
      onConfirm: (id) => {
        this.mapVote?.hide();
        this._applyMapChoice(id, mode);
      },
      onCancel: () => {
        this.mapVote?.hide();
        this.flowPage = 'modes';
        this._renderFlow();
      },
    });
    this.mapVote.show();
  }

  /**
   * A won map either is the one already built, or it is not — and if it is not,
   * the engine has to boot again, because the world is assembled once at init
   * from a single map. The playlist is remembered across that reload so the
   * player lands back on DEPLOY rather than at the top of the menu.
   */
  _applyMapChoice(id, mode) {
    if (!id || id === this._loadedMapId()) {
      this._startMatch(mode, id || this._loadedMapId());
      return;
    }
    const map = getMap(id);
    rememberMapId(id);
    try {
      sessionStorage.setItem(
        INTENT_KEY,
        JSON.stringify({ session: this.sessionType, mode, map: id, operator: this.opIndex })
      );
    } catch {
      /* the URL still carries the map; only the resume convenience is lost */
    }
    this._showMapLoading(map?.name ?? id);
    const params = new URLSearchParams(location.search);
    params.set('map', id);
    // A real navigation, not a soft reload: every subsystem has to be rebuilt
    // around the new level, and this is the one path that is guaranteed to.
    location.assign(`${location.pathname}?${params.toString()}`);
  }

  _showMapLoading(name) {
    const veil = this._el('div', 'rm-mapload', this.root);
    veil.setAttribute('role', 'status');
    this._el('div', 'rm-mapload-eyebrow', veil, 'DEPLOYING');
    this._el('div', 'rm-mapload-name', veil, String(name).toUpperCase());
    this._el('div', 'rm-mapload-bar', veil).appendChild(document.createElement('i'));
    this._el('div', 'rm-mapload-note', veil, 'BUILDING LEVEL GEOMETRY — THIS TAKES A MOMENT');
    this.root.style.display = '';
    veil.classList.add('rm-mapload-on');
  }

  /** Resume the deployment the player set up before a map reload, if any. */
  _resumeIntent() {
    let intent = null;
    try {
      const raw = sessionStorage.getItem(INTENT_KEY);
      if (raw) intent = JSON.parse(raw);
    } catch {
      intent = null;
    }
    if (!intent?.map) return false;
    // Only resume once the engine is actually holding the map that was voted for.
    if (intent.map !== this._loadedMapId()) return false;
    try {
      sessionStorage.removeItem(INTENT_KEY);
    } catch {
      /* nothing to clean up */
    }
    this.sessionType = intent.session === 'online' ? 'online' : 'offline';
    this.matchMode = intent.mode ?? 'TDM';
    if (Number.isInteger(intent.operator)) this._selectOperator(intent.operator);
    this.flowPage = 'ready';
    this._renderFlow();
    return true;
  }

  /**
   * Deploy on a decided map. Online, this always starts a fresh matchmaking
   * queue for exactly this (mode, map) — the map-vote step before this one no
   * longer holds a connection of its own (see `_openMapVote`), so there is
   * nothing here to reuse.
   */
  _startMatch(mode, mapId) {
    if (this.sessionType === 'online') {
      this.flowPage = 'queue';
      this._renderFlow();
      this._beginOnlineQueue(mode, mapId);
      return;
    }
    this._commitPlay({ session: this.sessionType, mode, map: mapId });
  }

  /**
   * Same boot-window race `_commitPlay`/`setOnPlay` guard against, but for the
   * online matchmaking handoff: a GO click can land before UiSystem has wired
   * `onQueue`, and unlike `onPlay` this used to be a plain `?.()` call — it
   * silently no-oped, leaving the player stuck on the "SEARCHING" screen
   * forever with no error and no retry.
   */
  _beginOnlineQueue(mode, mapId) {
    if (typeof this.onQueue !== 'function') {
      this._pendingQueue = { mode, mapId };
      this._flash('SYSTEM INITIALIZING — DEPLOY QUEUED');
      return;
    }
    const callbacks = {
      onStatus: (state) => this._setQueueStatus(state),
      onReady: () => {
        this._queueStop = null;
        this._onlineQueueActive = false;
        this._commitPlay({ session: 'online', mode, map: mapId });
      },
    };
    try {
      this._queueStop = this.onQueue({ session: 'online', mode, map: mapId }, callbacks) ?? null;
      this._onlineQueueActive = true;
    } catch (err) {
      console.warn('[mainmenu] matchmaking queue failed', err);
      this._setQueueStatus({ status: 'UNAVAILABLE', players: 0, required: 0, detail: 'MULTIPLAYER SERVICE COULD NOT START' });
    }
  }

  _cancelOnlineQueue() {
    try { this._queueStop?.(); } catch { /* cancellation is best effort */ }
    this._queueStop = null;
    this._onlineQueueActive = false;
    try { this.onQueueCancel?.(); } catch { /* the menu remains usable */ }
    this.queueState = null;
  }

  _buildFallback() {
    this.root = this._el('div', 'rm-root rm-fallback', null);
    this.root.style.display = 'none';
    const box = this._el('div', 'rm-fb-box', this.root);
    this._el('div', 'rm-fb-title', box, 'RIFT');
    const b = this._el('button', 'rm-fb-play', box, 'DEPLOY');
    b.type = 'button';
    b.addEventListener('click', () => this._commitPlay());
    this.navItems = [b];
    this.host.appendChild(this.root);
  }

  /* ---- small builders --------------------------------------------- */

  _serviceRecord(parent, id) {
    const card = this._el('div', 'rm-record', parent);
    const badge = this._el('div', 'rm-rank', card);
    badge.innerHTML = RANK_SVG;
    const info = this._el('div', 'rm-record-info', card);
    const top = this._el('div', 'rm-record-top', info);
    this.callsignEl = this._el('span', 'rm-callsign', top, id.callsign);
    this._el('span', 'rm-clearance', top, 'CLR ' + id.clearance);
    const sub = this._el('div', 'rm-record-sub', info);
    this.rankEl = this._el('span', null, sub, id.rank);
    this._el('span', 'rm-dot', sub);
    this._el('span', null, sub, 'PRESTIGE ' + id.prestige);
    this._el('span', 'rm-dot', sub);
    this.levelEl = this._el('span', 'rm-lvl', sub, 'LVL ' + id.level);
    // xp bar
    const xp = this._el('div', 'rm-xp', info);
    this.xpFill = this._el('i', null, xp);
    this.xpFill.style.width = (id.xp * 100).toFixed(1) + '%';
    const xpLbl = this._el('span', 'rm-xp-lbl', info);
    this.rankLevelEl = this._el('span', null, xpLbl, 'RANK ' + id.level);
    this.xpTextEl = this._el('span', 'rm-mono', xpLbl, id.xpNow + ' / ' + id.xpNext + ' XP');
    this.accountButton = this._el('button', 'rm-account-trigger', card, 'CREATE / SIGN IN');
    this.accountButton.type = 'button';
    this.accountButton.addEventListener('click', () => this._openAccount());
  }

  _telemetry(parent) {
    const tel = this._el('div', 'rm-telemetry', parent);
    const cells = [
      ['UPLINK', 'SECURE', 'ok'],
      ['PING', '24 MS', ''],
      ['SVR', 'EU-WEST-3', ''],
      ['LOAD', '61%', ''],
    ];
    for (const [k, v, mod] of cells) {
      const c = this._el('div', 'rm-tel-cell', tel);
      this._el('span', 'rm-tel-k', c, k);
      this._el('span', 'rm-tel-v' + (mod ? ' rm-tel-' + mod : ''), c, v);
    }
    // live-updating clock cell
    const clock = this._el('div', 'rm-tel-cell', tel);
    this._el('span', 'rm-tel-k', clock, 'LOCAL');
    this.clockEl = this._el('span', 'rm-tel-v rm-mono', clock, '--:--:--');
  }

  _squadTop(parent) {
    const cell = this._el('div', 'rm-squadtop', parent);
    this._el('span', 'rm-tag', cell, 'SQUAD');
    const av = this._el('span', 'rm-avatars', cell);
    for (let i = 0; i < 3; i++) this._el('i', i === 0 ? 'me' : null, av);
    this._el('span', 'rm-mono', cell, 'GHOST-6 · +2');
  }

  _panelHead(parent, title, sub) {
    const h = this._el('div', 'rm-panel-head', parent);
    const main = this._el('div', 'rm-panel-head-main', h);
    this._el('span', 'rm-panel-title', main, title);
    if (sub) this._el('span', 'rm-panel-sub', main, sub);
    return h;
  }

  _legend(parent, cls, label) {
    const l = this._el('span', 'rm-lg', parent);
    this._el('i', cls, l);
    this._el('span', null, l, label);
  }

  _warRow(parent, k, v, mod) {
    const r = this._el('div', 'rm-war-row', parent);
    this._el('span', 'rm-war-k', r, k);
    this._el('span', 'rm-war-v' + (mod ? ' rm-war-' + mod : ''), r, v);
  }

  _key(parent, label) {
    return this._el('span', 'rm-key', parent, label);
  }

  _refreshFile() {
    if (!this.fileRows) return;
    const op = OPERATORS[this.opIndex];
    const tab = this.opTab ?? 'bio';
    const locked = (op.unlockLevel ?? 1) > (this._identity().level ?? 1);
    const rowsByTab = {
      bio: [
        ['DESIGNATION', op.name],
        ['STATUS', locked ? `LOCKED — REQ. LVL ${op.unlockLevel}` : 'COMBAT READY'],
        ['CLEARANCE', 'ALPHA-3'],
        ['THEATRE', 'FRACTURE'],
      ],
      kit: [
        ['PRIMARY', op.weapon],
        ['KIT', op.kit],
        ['SIDEARM', op.sidearm],
        ['UTILITY', op.utility],
      ],
      history: [
        ['MISSIONS', String(op.missions)],
        ['K / D', op.kd.toFixed(2)],
        ['WIN RATE', op.winRate + '%'],
        ['STREAK', String(op.streak)],
      ],
    };
    this.fileRows.textContent = '';
    this.fileRows.parentElement?.classList.toggle('rm-op-locked', locked);
    for (const [k, v] of rowsByTab[tab] ?? rowsByTab.bio) {
      const r = this._el('div', 'rm-file-row' + (k === 'STATUS' ? ' rm-file-status' : ''), this.fileRows);
      this._el('span', 'rm-file-k', r, k);
      this._el('span', 'rm-file-v', r, v);
    }
    for (const b of this.opTabButtons ?? []) {
      b.classList.toggle('rm-on', b.dataset.tab === tab);
    }
    const fh = this.fileRows.parentElement.querySelector('.rm-panel-sub');
    if (fh) fh.textContent = op.name;
    if (this.heroSvgWrap) this.heroSvgWrap.dataset.op = op.id;
  }

  _setOpTab(tab) {
    this.opTab = tab;
    this._refreshFile();
  }

  /* ==================================================================== */
  /* interaction                                                          */
  /* ==================================================================== */

  _wire() {
    this._onKey = (e) => {
      if (!this.visible) return;
      if (this.accountModal && !this.accountModal.hidden) {
        if (e.key === 'Escape') this._closeAccount();
        // The form and its buttons retain their native keyboard behaviour;
        // never let menu navigation run through the dialog underneath them.
        e.stopPropagation();
        return;
      }
      if (this.specialModal && !this.specialModal.hidden) {
        if (e.key === 'Escape' || e.key === 'Enter') this._closeSpecialPopup();
        e.stopPropagation();
        return;
      }
      if (this.challengeModal && !this.challengeModal.hidden) {
        if (e.key === 'Escape') this._declineChallenge();
        e.stopPropagation();
        return;
      }
      if (this.flowPage !== 'home') {
        const buttons = [...(this.flowButtons ?? []), this.flowBack].filter(Boolean);
        const focused = buttons.indexOf(document.activeElement);
        const at = focused < 0 ? 0 : focused;
        if (e.key === 'Tab') {
          const direction = e.shiftKey ? -1 : 1;
          buttons[(at + direction + buttons.length) % buttons.length]?.focus({ preventScroll: true });
          e.preventDefault();
          return;
        }
        if (e.key === 'ArrowDown' || e.key === 's' || e.key === 'S') {
          buttons[(at + 1) % buttons.length]?.focus({ preventScroll: true });
          e.preventDefault();
          return;
        }
        if (e.key === 'ArrowUp' || e.key === 'w' || e.key === 'W') {
          buttons[(at - 1 + buttons.length) % buttons.length]?.focus({ preventScroll: true });
          e.preventDefault();
          return;
        }
        if (e.key === 'Escape') {
          this._flowBack();
          e.preventDefault();
          return;
        }
        // Buttons receive Enter/Space natively. All other keys stay inside the
        // modal instead of driving the muted home-screen navigation.
        return;
      }
      const n = this.navItems.length;
      const k = e.key;
      if (k === 'ArrowDown' || k === 's' || k === 'S') {
        this._select((this.selected + 1) % n, true); e.preventDefault();
      } else if (k === 'ArrowUp' || k === 'w' || k === 'W') {
        this._select((this.selected - 1 + n) % n, true); e.preventDefault();
      } else if (k === 'Enter' || k === ' ') {
        this._activate(this.selected); e.preventDefault();
      } else if (k === 'ArrowRight' || k === 'd' || k === 'D') {
        this._selectOperator((this.opIndex + 1) % OPERATORS.length); e.preventDefault();
      } else if (k === 'ArrowLeft' || k === 'a' || k === 'A') {
        this._selectOperator((this.opIndex - 1 + OPERATORS.length) % OPERATORS.length); e.preventDefault();
      }
    };
    document.addEventListener('keydown', this._onKey);
  }

  _select(i, focus) {
    this.selected = i;
    for (let k = 0; k < this.navItems.length; k++) {
      this.navItems[k].classList.toggle('rm-on', k === i);
    }
    if (focus && this.navItems[i]) this.navItems[i].focus({ preventScroll: true });
  }

  _activate(i) {
    const item = NAV[i];
    if (!item) return this._commitPlay();
    switch (item.id) {
      case 'play':
        this._showSession();
        break;
      case 'operators':
        this._openPersonnel();
        break;
      case 'loadout':
        this._openArmory();
        break;
      case 'leaderboard':
        this._openLeaderboard();
        break;
      case 'settings':
        this._openSettings();
        break;
      case 'quit':
        this._flash('SESSION HELD');
        break;
      default:
        this._flash(item.label.toUpperCase() + ' — COMING ONLINE');
    }
  }

  _selectOperator(i) {
    this.opIndex = i;
    this._refreshFile();
    // ask the live-3D layer to swap variants if it is running
    if (this._three) this._three.want = OPERATORS[i].id;
    // trigger the scan sweep across the portrait
    if (this.opPortrait) {
      this.opPortrait.classList.remove('rm-op-flash');
      void this.opPortrait.offsetWidth;
      this.opPortrait.classList.add('rm-op-flash');
    }
  }

  _flash(msg) {
    const sub = this.root.querySelector('.rm-deploy .rm-panel-sub');
    if (!sub) return;
    if (!sub.dataset.base) sub.dataset.base = sub.textContent;
    sub.textContent = msg;
    sub.classList.add('rm-flash');
    clearTimeout(this._flashT);
    this._flashT = setTimeout(() => {
      sub.textContent = sub.dataset.base;
      sub.classList.remove('rm-flash');
    }, 1400);
  }

  /** The single path that starts the game — always safe, always works. */
  _commitPlay(selection = null) {
    // The host onPlay is the authority on enabling the player + pointer lock,
    // and it runs inside this very click gesture so pointer-lock is granted.
    // The home screen is visible while the engine boots, before UiSystem has
    // attached this callback. Keep it visible until gameplay is actually ready.
    if (typeof this.onPlay !== 'function') {
      this._pendingPlay = selection;
      this._hasPendingPlay = true;
      // The flash lands in the right-hand rail, which is dimmed and inert while
      // this screen is open — on its own it reads as nothing having happened,
      // which is exactly how this looked when it was broken. Mark the button.
      trace('GO pressed -> queued (engine still booting)');
      this._setDeployPending();
      this._flash('SYSTEM INITIALIZING — DEPLOY QUEUED');
      return;
    }
    try {
      // A verified queue is now becoming a match; hiding the screen must not
      // execute its normal queue-cancel cleanup and tear networking back down.
      this._matchStarting = true;
      trace('GO pressed -> onPlay', selection);
      this.onPlay(selection);
    } catch (err) {
      trace('onPlay THREW', String(err?.message ?? err));
      console.warn('[mainmenu] onPlay handler threw', err);
      // The match did NOT start, so this is an ordinary teardown again: leaving
      // the flag set makes hide() skip its queue-cancel and lobby disconnect.
      this._matchStarting = false;
      this.hide();
    }
  }

  /** Show the pressed GO button as accepted-and-waiting, not ignored. */
  _setDeployPending() {
    const go = this.flowButtons?.[0];
    if (!go) return;
    go.disabled = true;
    go.classList.add('rm-flow-pending');
    const label = go.querySelector('.rm-flow-choice-label');
    if (label) label.textContent = 'WAIT';
    if (this.flowStep) this.flowStep.textContent = 'DEPLOYING — BOOTING SYSTEMS';
    // Boot services this within a second or two of the engine coming up. If it
    // has not, the level is still building (or something is wedged) — an
    // unexplained spinner that never resolves is worse than saying so.
    clearTimeout(this._pendingT);
    this._pendingT = setTimeout(() => {
      if (!this._hasPendingPlay || !this.flowStep) return;
      this.flowStep.textContent = 'STILL BUILDING THE LEVEL — DEPLOY RUNS AS SOON AS IT IS READY';
    }, 10000);
  }

  /**
   * Attach the gameplay handoff.
   *
   * A GO click made before this lands is deliberately NOT replayed here. This
   * runs from UiSystem.init(), which is the 10th of 13 systems to boot: the
   * UiSystem around it is still half-constructed (`this.state` is assigned
   * several lines later, so startMatch's setMatch() would throw on it), and
   * `net`/`audio`/`lobby` have not initialized at all. Boot services the
   * queued click through flushQueuedDeploy() once the engine is actually up.
   */
  setOnPlay(onPlay) {
    this.onPlay = typeof onPlay === 'function' ? onPlay : null;
  }

  /** Attach the matchmaking handoff. Queued clicks flush from boot, as above. */
  setOnQueue(onQueue) {
    this.onQueue = typeof onQueue === 'function' ? onQueue : null;
  }

  /**
   * Run a deploy the player asked for before the engine could service it.
   * Called from boot once every system has initialized and the frame loop is
   * running, so the match starts into a finished engine rather than a
   * half-built one. Returns whether there was anything to run.
   */
  /** Whether the player pressed GO before the engine could service it. */
  hasQueuedDeploy() {
    return Boolean(this._hasPendingPlay || this._pendingQueue);
  }

  flushQueuedDeploy() {
    clearTimeout(this._pendingT);
    if (this._hasPendingPlay && typeof this.onPlay === 'function') {
      const selection = this._pendingPlay;
      this._pendingPlay = null;
      this._hasPendingPlay = false;
      this._commitPlay(selection);
      return true;
    }
    if (this._pendingQueue && typeof this.onQueue === 'function') {
      const { mode, mapId } = this._pendingQueue;
      this._pendingQueue = null;
      this._beginOnlineQueue(mode, mapId);
      return true;
    }
    return false;
  }

  /* ==================================================================== */
  /* lifecycle                                                            */
  /* ==================================================================== */

  show() {
    if (this._disposed) return;
    // A pre-engine homescreen is already visible when UiSystem calls showHome.
    // Retry its optional 3D operator enhancement now that AI materials exist.
    if (this.visible) {
      this._tryOperator3D();
      return;
    }
    this.visible = true;
    this._refreshProgression();
    this._matchStarting = false;
    this.flowPage = 'home';
    this._setFlowOpen(false);
    this.root.style.display = '';
    // force reflow, then run the entrance stagger
    void this.root.offsetWidth;
    this.root.classList.add('rm-live');
    // The input listener lives on `window` and otherwise locks the mouse on
    // every left click, including clicks on ONLINE/OFFLINE and mode cards.
    // Menu buttons use their own listeners, so disabling gameplay input here
    // keeps the cursor free without making the menu inert.
    try { this.ctx?.input && (this.ctx.input.enabled = false); } catch { /* optional */ }
    // Park the player so nothing drives the game behind the menu.
    try { this.ctx?.peek?.('player')?.setControlEnabled?.(false); } catch { /* optional */ }
    try { document.exitPointerLock?.(); } catch { /* optional */ }
    this._startLoop();
    // progressive enhancement — never blocks, never required
    this._tryOperator3D();
    // A map vote that caused a reload left its playlist behind; pick the flow up
    // where the player left it instead of making them choose twice.
    let resumed = false;
    try {
      resumed = this._resumeIntent();
    } catch (err) {
      console.warn('[mainmenu] could not resume deployment intent', err);
      // Whatever partial state _resumeIntent left behind (e.g. the flow marked
      // open, the stage marked inert) must not survive a failed resume — an
      // inert stage with no visible flow panel over it reads as "the menu is
      // just dead" with no error in sight.
      this.flowPage = 'home';
      this._setFlowOpen(false);
    }
    // move keyboard focus onto the primary action for accessibility
    if (!resumed) {
      setTimeout(() => { try { this.navItems[0]?.focus({ preventScroll: true }); } catch {} }, 60);
    }
  }

  hide() {
    if (!this.visible) return;
    this.visible = false;
    if (!this._matchStarting) {
      this._cancelOnlineQueue();
      try { this.ctx?.peek?.('lobby')?.disconnect(); } catch { /* best effort */ }
    }
    this.mapVote?.hide();
    this.root.classList.remove('rm-live');
    this.root.style.display = 'none';
    this._stopLoop();
    this._teardown3D();
  }

  dispose() {
    this._disposed = true;
    this.hide();
    try { document.removeEventListener('keydown', this._onKey); } catch {}
    clearTimeout(this._flashT);
    this._teardown3D();
    try { this.mapVote?.dispose(); } catch {}
    this.mapVote = null;
    try { this.armory?.dispose(); } catch {}
    this.armory = null;
    try { this.personnel?.dispose(); } catch {}
    this.personnel = null;
    try { this.leaderboard?.dispose(); } catch {}
    this.leaderboard = null;
    try { this.settings?.dispose(); } catch {}
    this.settings = null;
    this._offLobbyRoster?.();
    this._offLobbyChallenge?.();
    this._offLobbyChallengeSent?.();
    this._offLobbyChallengeDeclined?.();
    this._offLobbyChallengeExpired?.();
    this._offProgression?.();
    try { this.root?.remove(); } catch {}
    // leave the stylesheet — cheap, and other instances may still use it
  }

  /* ==================================================================== */
  /* ambient loop (clock + cheap per-frame work + 3D drive)               */
  /* ==================================================================== */

  _startLoop() {
    if (this._raf) return;
    let last = performance.now();
    // The clock is sampled every frame; mutate one Date instead of allocating
    // sixty short-lived objects per second while the front end is open.
    const clock = new Date();
    const tick = (now) => {
      if (!this.visible) { this._raf = 0; return; }
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      this._t += dt;
      if (this.clockEl) {
        clock.setTime(Date.now());
        const s = `${p2(clock.getHours())}:${p2(clock.getMinutes())}:${p2(clock.getSeconds())}`;
        if (this.clockEl.textContent !== s) this.clockEl.textContent = s;
      }
      // war room — rotate the live-feed line every ~3.6s, and animate the
      // server-bar fill slightly so the panel always looks alive
      if (this.warLiveText) {
        this._warT = (this._warT ?? 0) + dt;
        if (this._warT > 3.6) {
          this._warT = 0;
          this._warIdx = (this._warIdx + 1) % this._warLines.length;
          this.warLiveText.textContent = this._warLines[this._warIdx];
        }
      }
      if (this._three) this._drive3D(dt);
      this._raf = requestAnimationFrame(tick);
    };
    this._raf = requestAnimationFrame(tick);
  }

  _stopLoop() {
    if (this._raf) cancelAnimationFrame(this._raf);
    this._raf = 0;
  }

  /* ==================================================================== */
  /* optional live-3D operator (guarded progressive enhancement)          */
  /* ==================================================================== */

  /**
   * Attempt to render the real operator geometry — in its patrol-carry bind pose,
   * which already holds the carbine — in a small dedicated WebGL context layered
   * over the SVG. EVERYTHING here is guarded: any failure silently leaves the
   * guaranteed SVG hero in place. It never disposes engine-owned geometry.
   */
  async _tryOperator3D() {
    if (this._three || this._disposed) return;
    let THREE, ai, rig;
    try {
      ai = this.ctx?.get?.('ai') ?? this.ctx?.peek?.('ai');
      if (!ai || typeof ai.variant !== 'function') return;
      THREE = await import('three');
      const rigMod = await import('../ai/rig.js').catch(() => null);
      rig = rigMod?.RIG;
      if (!rig || typeof rig.createSkeleton !== 'function') return;
    } catch { return; }
    if (this._disposed || !this.visible) return;

    try {
      const wrap = this.heroCanvasWrap;
      const w = Math.max(2, wrap.clientWidth | 0) || 520;
      const h = Math.max(2, wrap.clientHeight | 0) || 760;

      const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, powerPreference: 'low-power' });
      renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
      renderer.setSize(w, h, false);
      renderer.setClearColor(0x000000, 0);
      if ('outputColorSpace' in renderer) renderer.outputColorSpace = THREE.SRGBColorSpace;
      const cv = renderer.domElement;
      cv.className = 'rm-3d';
      wrap.appendChild(cv);

      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(30, w / h, 0.1, 50);
      camera.position.set(0.05, 1.15, 3.15);
      camera.lookAt(0, 1.12, 0);

      // Cold key + amber rim + soft fill. A monochrome clay material keeps us
      // independent of the engine's patched, renderer-specific shaders.
      const key = new THREE.DirectionalLight(0xbcd2e6, 1.35);
      key.position.set(-2.2, 3.0, 2.4);
      const rim = new THREE.DirectionalLight(0xffb02a, 2.4);
      rim.position.set(2.6, 2.2, -2.2);
      const fill = new THREE.HemisphereLight(0x2a3746, 0x05070a, 0.7);
      scene.add(key, rim, fill);

      const pivot = new THREE.Group();
      scene.add(pivot);

      this._three = {
        THREE, ai, rig, renderer, scene, camera, pivot, cv,
        current: null, currentId: null, want: OPERATORS[this.opIndex].id,
        sway: 0, ok: false,
      };
      this._mountVariant(OPERATORS[this.opIndex].id);
    } catch (err) {
      console.warn('[mainmenu] live-3D unavailable, SVG hero retained', err);
      this._teardown3D();
    }
  }

  _mountVariant(id) {
    const S = this._three;
    if (!S) return;
    try {
      const variant = S.ai.variant(id);
      if (!variant?.geometry) return;
      const THREE = S.THREE;

      // fresh skeleton (per-instance) posed to the bind carry
      const { skeleton, root } = S.rig.createSkeleton();
      // single matte clay material — one Material, so grouped geometry draws whole
      const materials = variant.materials.map((material) => material.clone());
      const mesh = new THREE.SkinnedMesh(variant.geometry, materials);
      mesh.frustumCulled = false;
      const holder = new THREE.Group();
      holder.add(root);
      holder.add(mesh);
      mesh.bind(skeleton);
      // frame the upper three-quarter: drop the feet below frame, face 3/4 left
      holder.position.y = -0.62;
      holder.rotation.y = -0.35;

      // swap in
      if (S.current) { S.pivot.remove(S.current.holder); this._disposeSkel(S.current); }
      S.pivot.add(holder);
      S.current = { holder, mesh, skeleton, materials };
      S.currentId = id;

      // first successful frame → reveal the canvas over the SVG
      S.renderer.render(S.scene, S.camera);
      S.ok = true;
      this.heroStage.classList.add('rm-3d-live');
    } catch (err) {
      console.warn('[mainmenu] variant mount failed', err);
    }
  }

  _drive3D(dt) {
    const S = this._three;
    if (!S || !S.ok) return;
    try {
      if (S.want && S.want !== S.currentId) this._mountVariant(S.want);
      // precise, mechanical oscillation — a rotating-platform vignette, not a spin
      S.sway += dt;
      S.pivot.rotation.y = Math.sin(S.sway * 0.35) * 0.22;
      S.renderer.render(S.scene, S.camera);
    } catch {
      // a runtime GL hiccup must not kill the menu — drop back to SVG
      this._teardown3D();
    }
  }

  _disposeSkel(cur) {
    try {
      cur.mesh?.skeleton?.boneTexture?.dispose?.();
      cur.skeleton?.dispose?.();
      for (const material of cur.materials ?? []) material.dispose?.();
    } catch {}
    // NB: never dispose cur.mesh.geometry — it is engine-owned and shared.
  }

  _teardown3D() {
    const S = this._three;
    this._three = null;
    try { this.heroStage?.classList.remove('rm-3d-live'); } catch {}
    if (!S) return;
    try { if (S.current) this._disposeSkel(S.current); } catch {}
    try {
      S.renderer.forceContextLoss?.();
      S.renderer.dispose?.();
      S.cv?.remove?.();
    } catch {}
  }
}

/* ---- prototype helpers (kept off the main body for readability) ------ */

/** Best-effort read of the player's identity for the service record. */
MainMenu.prototype._identity = function () {
  let level = 1;
  let xpTotal = 0;
  try {
    const p = this.ctx?.peek?.('player');
    if (p && typeof p.level === 'number') level = p.level;
    if (p && typeof p.xp === 'number') xpTotal = p.xp;
  } catch { /* stub-safe */ }
  // `xpTotal` is the server's lifetime total, earned only from confirmed
  // kills of real players (see `NetSystem._applySelfInfo`); this just folds
  // it into the current level's progress for the bar.
  const xpNext = 1000;
  const xpNow = xpTotal % xpNext;
  return {
    callsign: this.account?.username ?? 'RVN-ACTUAL',
    clearance: 'ALPHA-3',
    rank: level >= 20 ? 'VETERAN' : level >= 10 ? 'SPECIALIST' : level >= 5 ? 'OPERATOR' : 'RECRUIT',
    prestige: '0',
    level,
    xp: xpNow / xpNext,
    xpNow: xpNow.toLocaleString(),
    xpNext: xpNext.toLocaleString(),
  };
};

/** Repaint the service record after player or account progression arrives. */
MainMenu.prototype._refreshProgression = function () {
  const id = this._identity();
  if (this.callsignEl) this.callsignEl.textContent = id.callsign;
  if (this.rankEl) this.rankEl.textContent = id.rank;
  if (this.levelEl) this.levelEl.textContent = `LVL ${id.level}`;
  if (this.rankLevelEl) this.rankLevelEl.textContent = `RANK ${id.level}`;
  if (this.xpTextEl) this.xpTextEl.textContent = `${id.xpNow} / ${id.xpNext} XP`;
  if (this.xpFill) this.xpFill.style.width = `${(id.xp * 100).toFixed(1)}%`;
  this._refreshFile();
};

MainMenu.prototype._quality = function () {
  try { return (this.ctx?.config?.quality || '').toUpperCase(); } catch { return ''; }
};

/* ---- tiny helpers ---------------------------------------------------- */

function p2(n) { return n < 10 ? '0' + n : '' + n; }

/* ---- generative helpers for the dial + deployment cube --------------- */
function dialTicks(cx, cy, r1, r2, count) {
  let out = '';
  const majorEvery = count / 12;
  for (let i = 0; i < count; i++) {
    const a = (i / count) * Math.PI * 2 - Math.PI / 2;
    const major = i % majorEvery === 0;
    const rr1 = major ? r1 - 10 : r1;
    const x1 = cx + Math.cos(a) * rr1, y1 = cy + Math.sin(a) * rr1;
    const x2 = cx + Math.cos(a) * r2, y2 = cy + Math.sin(a) * r2;
    out += `<line x1="${x1.toFixed(1)}" y1="${y1.toFixed(1)}" x2="${x2.toFixed(1)}" y2="${y2.toFixed(1)}" stroke="${major ? 'rgba(255,176,42,.55)' : 'rgba(140,180,205,.28)'}" stroke-width="${major ? 1.4 : 0.8}"/>`;
  }
  return out;
}

function dialLabels(cx, cy, r, count) {
  let out = '';
  for (let i = 0; i < count; i++) {
    const a = (i / count) * Math.PI * 2 - Math.PI / 2;
    const x = cx + Math.cos(a) * r, y = cy + Math.sin(a) * r;
    const val = String(Math.round((i * 360) / count)).padStart(3, '0');
    out += `<text x="${x.toFixed(1)}" y="${y.toFixed(1)}" text-anchor="middle" dominant-baseline="middle" font-size="10" letter-spacing="1" fill="rgba(180,205,222,.5)" font-family="ui-monospace,monospace">${val}</text>`;
  }
  return out;
}

function isoLerp(a, b, t) { return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]; }

function isoPoint(p0, p1, p2, p3, t, u) {
  return isoLerp(isoLerp(p0, p1, t), isoLerp(p3, p2, t), u);
}

/** Grid lines across an iso quad walked p0->p1->p2->p3 (p0-p1 parallel to p3-p2). */
function isoGrid(p0, p1, p2, p3, n, stroke) {
  let out = '';
  for (let i = 1; i < n; i++) {
    const t = i / n;
    const a = isoLerp(p0, p1, t), b = isoLerp(p3, p2, t);
    const c = isoLerp(p0, p3, t), d = isoLerp(p1, p2, t);
    out += `<line x1="${a[0].toFixed(1)}" y1="${a[1].toFixed(1)}" x2="${b[0].toFixed(1)}" y2="${b[1].toFixed(1)}" stroke="${stroke}" stroke-width="0.6"/>`;
    out += `<line x1="${c[0].toFixed(1)}" y1="${c[1].toFixed(1)}" x2="${d[0].toFixed(1)}" y2="${d[1].toFixed(1)}" stroke="${stroke}" stroke-width="0.6"/>`;
  }
  return out;
}

function cubeMarker(p0, p1, p2, p3, t, u, kind) {
  const [x, y] = isoPoint(p0, p1, p2, p3, t, u);
  if (kind === 'sq') return `<rect x="${(x - 2).toFixed(1)}" y="${(y - 2).toFixed(1)}" width="4" height="4"/>`;
  if (kind === 'tri') return `<path d="M${x.toFixed(1)} ${(y - 3).toFixed(1)} L${(x + 3).toFixed(1)} ${(y + 3).toFixed(1)} L${(x - 3).toFixed(1)} ${(y + 3).toFixed(1)} Z"/>`;
  return `<rect x="${(x - 2.5).toFixed(1)}" y="${(y - 2.5).toFixed(1)}" width="5" height="5" fill="#ffb02a" transform="rotate(45 ${x.toFixed(1)} ${y.toFixed(1)})"/>
    <circle class="rm-map-ping" cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="6" fill="none" stroke="#ffb02a" stroke-width="1.2"/>`;
}

/* ---- center PLAY dial: a rotating armillary ring over a bearing ring --- */
const DIAL_SVG = `
<svg class="rm-dial-svg" viewBox="0 0 600 600" aria-hidden="true">
  <defs>
    <linearGradient id="dialGold" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stop-color="#ffe3a8"/>
      <stop offset="0.5" stop-color="#ffb02a"/>
      <stop offset="1" stop-color="#8f5c0f"/>
    </linearGradient>
    <linearGradient id="dialBlue" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stop-color="#bfe9ff"/>
      <stop offset="0.5" stop-color="#5fb3e0"/>
      <stop offset="1" stop-color="#1c3f52"/>
    </linearGradient>
    <radialGradient id="dialHub" cx="0.4" cy="0.35" r="0.8">
      <stop offset="0" stop-color="#1c242c"/>
      <stop offset="0.7" stop-color="#0c1116"/>
      <stop offset="1" stop-color="#05070a"/>
    </radialGradient>
  </defs>

  <circle cx="300" cy="300" r="272" fill="none" stroke="rgba(120,170,200,.3)" stroke-width="1"/>
  <circle cx="300" cy="300" r="246" fill="none" stroke="rgba(255,176,42,.22)" stroke-width="1"/>
  <g>${dialTicks(300, 300, 236, 250, 72)}</g>
  <g>${dialLabels(300, 300, 220, 8)}</g>

  <g transform="rotate(18 300 300)"><g class="rm-ring rm-ring-a"><ellipse cx="300" cy="300" rx="200" ry="86" fill="none" stroke="url(#dialGold)" stroke-width="3.2" opacity="0.9"/></g></g>
  <g transform="rotate(-32 300 300)"><g class="rm-ring rm-ring-b"><ellipse cx="300" cy="300" rx="196" ry="80" fill="none" stroke="url(#dialBlue)" stroke-width="2.2" opacity="0.6"/></g></g>
  <g transform="rotate(72 300 300)"><g class="rm-ring rm-ring-c"><ellipse cx="300" cy="300" rx="172" ry="64" fill="none" stroke="url(#dialGold)" stroke-width="1.6" opacity="0.5"/></g></g>

  <circle cx="300" cy="300" r="112" fill="url(#dialHub)" stroke="rgba(255,176,42,.4)" stroke-width="1.5"/>
  <circle cx="300" cy="300" r="118" fill="none" stroke="rgba(190,214,230,.18)" stroke-width="1"/>
</svg>`;

/* ---- outer dial bezel: a larger concentric ring that sits behind the
   armillary dial and slowly counter-rotates, with eight amber index marks
   and four cardinal labels (N E S W in custom tactical notation). */
const BEZEL_SVG = `
<svg class="rm-bezel-svg" viewBox="0 0 800 800" aria-hidden="true">
  <defs>
    <linearGradient id="bezelStroke" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stop-color="rgba(255,176,42,.8)"/>
      <stop offset="0.5" stop-color="rgba(255,176,42,.35)"/>
      <stop offset="1" stop-color="rgba(255,176,42,.15)"/>
    </linearGradient>
  </defs>
  <g class="rm-bezel-rot">
    <circle cx="400" cy="400" r="378" fill="none" stroke="rgba(190,214,230,.18)" stroke-width="1"/>
    <circle cx="400" cy="400" r="360" fill="none" stroke="url(#bezelStroke)" stroke-width="2"/>
    <circle cx="400" cy="400" r="346" fill="none" stroke="rgba(190,214,230,.12)" stroke-width="1" stroke-dasharray="2 6"/>
  </g>
  <g>
    <circle cx="400" cy="400" r="384" fill="none" stroke="rgba(255,176,42,.5)" stroke-width="2" stroke-dasharray="120 1800" stroke-linecap="round" class="rm-bezel-arc-a"/>
    <circle cx="400" cy="400" r="384" fill="none" stroke="rgba(121,210,255,.4)" stroke-width="2" stroke-dasharray="40 1900" stroke-linecap="round" class="rm-bezel-arc-b"/>
  </g>
  ${(() => {
    const cx = 400, cy = 400, r = 332, count = 24;
    let out = '';
    for (let i = 0; i < count; i++) {
      const a = (i / count) * Math.PI * 2 - Math.PI / 2;
      const major = i % 6 === 0;
      const rr1 = major ? r - 8 : r;
      const x1 = cx + Math.cos(a) * rr1, y1 = cy + Math.sin(a) * rr1;
      const x2 = cx + Math.cos(a) * (r + (major ? 14 : 8)), y2 = cy + Math.sin(a) * (r + (major ? 14 : 8));
      out += `<line x1="${x1.toFixed(1)}" y1="${y1.toFixed(1)}" x2="${x2.toFixed(1)}" y2="${y2.toFixed(1)}" stroke="${major ? 'rgba(255,176,42,.85)' : 'rgba(190,214,230,.4)'}" stroke-width="${major ? 2.4 : 1.4}"/>`;
    }
    return out;
  })()}
  ${(() => {
    const labels = [['N', 400, 56], ['E', 744, 412], ['S', 400, 760], ['W', 56, 412]];
    return labels.map(([t, x, y]) => `<text x="${x}" y="${y}" text-anchor="middle" dominant-baseline="middle" font-size="22" letter-spacing="4" fill="rgba(255,176,42,.75)" font-family="ui-monospace,monospace" font-weight="700">${t}</text>`).join('');
  })()}
</svg>`;

/* ---- deployment stub: an isometric wireframe grid cube ---------------- */
const CUBE_T = [120, 26];
const CUBE_TL = [42, 66];
const CUBE_TR = [198, 66];
const CUBE_TB = [120, 106];
const CUBE_B = [120, 102];
const CUBE_BL = [42, 142];
const CUBE_BR = [198, 142];

const CUBE_SVG = `
<svg viewBox="0 0 240 170" aria-hidden="true">
  <defs>
    <linearGradient id="cubeTop" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color="#22323c" stop-opacity="0.55"/>
      <stop offset="1" stop-color="#0d161c" stop-opacity="0.4"/>
    </linearGradient>
    <linearGradient id="cubeLeft" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stop-color="#152029" stop-opacity="0.6"/>
      <stop offset="1" stop-color="#080e12" stop-opacity="0.5"/>
    </linearGradient>
    <linearGradient id="cubeRight" x1="1" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color="#1b2830" stop-opacity="0.6"/>
      <stop offset="1" stop-color="#0a1116" stop-opacity="0.5"/>
    </linearGradient>
  </defs>
  <rect width="240" height="170" fill="#060b0f"/>
  <polygon points="120,26 198,66 120,106 42,66" fill="url(#cubeTop)" stroke="#3a5464" stroke-width="1"/>
  <polygon points="120,26 42,66 42,142 120,102" fill="url(#cubeLeft)" stroke="#2a3c45" stroke-width="1"/>
  <polygon points="120,26 198,66 198,142 120,102" fill="url(#cubeRight)" stroke="#2a3c45" stroke-width="1"/>
  <g opacity="0.6">
    ${isoGrid(CUBE_T, CUBE_TR, CUBE_TB, CUBE_TL, 4, '#3a5464')}
    ${isoGrid(CUBE_T, CUBE_TL, CUBE_BL, CUBE_B, 4, '#283942')}
    ${isoGrid(CUBE_T, CUBE_TR, CUBE_BR, CUBE_B, 4, '#283942')}
  </g>
  <g fill="#79d2ff">
    ${cubeMarker(CUBE_T, CUBE_TL, CUBE_BL, CUBE_B, 0.62, 0.4, 'sq')}
    ${cubeMarker(CUBE_T, CUBE_TR, CUBE_BR, CUBE_B, 0.3, 0.55, 'sq')}
    ${cubeMarker(CUBE_T, CUBE_TR, CUBE_TB, CUBE_TL, 0.4, 0.7, 'sq')}
  </g>
  <g fill="#ff5b47">
    ${cubeMarker(CUBE_T, CUBE_TL, CUBE_BL, CUBE_B, 0.35, 0.75, 'tri')}
    ${cubeMarker(CUBE_T, CUBE_TR, CUBE_BR, CUBE_B, 0.68, 0.3, 'tri')}
    ${cubeMarker(CUBE_T, CUBE_TR, CUBE_BR, CUBE_B, 0.5, 0.82, 'tri')}
  </g>
  ${cubeMarker(CUBE_T, CUBE_TR, CUBE_TB, CUBE_TL, 0.62, 0.35, 'obj')}
  <polygon points="120,26 198,66 120,106 42,66" fill="none" stroke="#4a6a7c" stroke-width="1.2"/>
  <polygon points="120,26 42,66 42,142 120,102" fill="none" stroke="#33474f" stroke-width="1.2"/>
  <polygon points="120,26 198,66 198,142 120,102" fill="none" stroke="#33474f" stroke-width="1.2"/>
</svg>`;

/* ====================================================================== */
/* Scoped stylesheet. Everything is `.rm-` prefixed.                       */
/* ====================================================================== */

const CSS = `
.rm-root, .rm-root * { margin:0; padding:0; box-sizing:border-box; }
.rm-root {
  --amber:#ffb02a; --amber-2:#ffc251; --amber-3:#ffd884; --red:#ff3f31; --cyan:#79d2ff; --cyan-2:#9fe1ff; --ok:#a8e86a;
  --magenta:#ff5c8a; --violet:#9c6dff;
  --oc:#080c10; --ink:#f0f6fb; --ink-2:rgba(220,232,243,.85);
  --ink-3:rgba(180,200,215,.55); --hair:rgba(255,176,42,.35); --hair-2:rgba(255,176,42,.18);
  --panel: rgba(8,13,17,.88); --panel-2: rgba(10,16,21,.75);
  --glow-amber: 0 0 24px rgba(255,176,42,.65), 0 0 60px rgba(255,176,42,.25);
  --glow-cyan: 0 0 24px rgba(121,210,255,.65), 0 0 60px rgba(121,210,255,.25);
  --ff:"Avenir Next Condensed","DIN Alternate","Roboto Condensed","Arial Narrow","Helvetica Neue",Inter,system-ui,sans-serif;
  --fd:"DIN Condensed","Avenir Next Condensed","Oswald","Arial Narrow",Impact,system-ui,sans-serif;
  --fm:"SF Mono",ui-monospace,"Roboto Mono",Menlo,monospace;
  --shear: polygon(0 0, 100% 0, 100% calc(100% - 12px), calc(100% - 12px) 100%, 0 100%);
  position:fixed; inset:0; z-index:40;
  font-family:var(--ff); color:var(--ink); text-transform:uppercase;
  letter-spacing:.08em; font-weight:600; -webkit-font-smoothing:antialiased;
  font-variant-numeric:tabular-nums; user-select:none; overflow:hidden;
  background: rgba(4,6,9,.30);
  -webkit-backdrop-filter: blur(7px) saturate(1.1) brightness(1);
          backdrop-filter: blur(7px) saturate(1.1) brightness(1);
}

/* ---------- living background ---------------------------------------- */
.rm-bg { position:absolute; inset:0; overflow:hidden; }
.rm-bg-grad { position:absolute; inset:0;
  background:
    radial-gradient(120% 90% at 72% 32%, rgba(58,108,148,.95) 0%, rgba(6,10,14,0) 52%),
    radial-gradient(70% 60% at 14% 88%, rgba(122,68,18,.85) 0%, rgba(6,10,14,0) 58%),
    radial-gradient(50% 50% at 50% 100%, rgba(160,55,110,.32) 0%, rgba(6,10,14,0) 60%),
    linear-gradient(125deg, rgba(2,4,7,.98) 0%, rgba(8,14,20,.92) 38%, rgba(14,22,30,.7) 78%, rgba(8,14,20,.85) 100%);
}
.rm-bg-haze { position:absolute; inset:-10%; filter:blur(50px); opacity:.9; }
.rm-bg-haze i { position:absolute; border-radius:50%; mix-blend-mode:screen; }
.rm-bg-haze i:nth-child(1){ width:42vw; height:42vw; left:50%; top:6%;
  background:radial-gradient(circle,rgba(110,170,210,.85),transparent 65%); animation:rm-drift 26s ease-in-out infinite; }
.rm-bg-haze i:nth-child(2){ width:34vw; height:34vw; left:4%; top:50%;
  background:radial-gradient(circle,rgba(255,160,40,.7),transparent 65%); animation:rm-drift 34s ease-in-out infinite reverse; }
.rm-bg-haze i:nth-child(3){ width:30vw; height:30vw; left:68%; top:58%;
  background:radial-gradient(circle,rgba(80,140,180,.78),transparent 65%); animation:rm-drift 30s ease-in-out infinite; }
@keyframes rm-drift { 0%,100%{ transform:translate3d(0,0,0);} 50%{ transform:translate3d(-3%,-4%,0);} }

/* Diagonal energy beam sweeping across the whole stage */
.rm-bg-beam { position:absolute; inset:-20%;
  background: linear-gradient(105deg, transparent 38%, rgba(255,176,42,.14) 48%, transparent 58%),
              linear-gradient(105deg, transparent 60%, rgba(121,210,255,.1) 68%, transparent 78%);
  animation: rm-beam 14s linear infinite; mix-blend-mode: screen; }
@keyframes rm-beam { 0%{ transform: translateX(-12%); } 100%{ transform: translateX(12%); } }

.rm-motes { position:absolute; inset:0; }
.rm-motes i { position:absolute; bottom:-4%; left:var(--x);
  width:calc(2px * var(--s)); height:calc(2px * var(--s)); border-radius:50%;
  background:rgba(220,234,250,.6); box-shadow:0 0 8px rgba(170,210,240,.8), 0 0 16px rgba(255,176,42,.2);
  animation: rm-rise-mote var(--d) linear infinite; animation-delay:var(--delay); opacity:0; }
@keyframes rm-rise-mote {
  0%{ transform:translateY(0); opacity:0; }
  10%{ opacity:.85; } 90%{ opacity:.55; }
  100%{ transform:translateY(-108vh); opacity:0; }
}
.rm-grid { position:absolute; inset:0; opacity:.45;
  background-image:linear-gradient(rgba(120,160,190,.28) 1px,transparent 1px),
                   linear-gradient(90deg,rgba(120,160,190,.28) 1px,transparent 1px);
  background-size:46px 46px;
  -webkit-mask-image:radial-gradient(120% 100% at 50% 40%,#000 20%,transparent 80%);
          mask-image:radial-gradient(120% 100% at 50% 40%,#000 20%,transparent 80%); }
.rm-grid::before { content:''; position:absolute; inset:0;
  background: linear-gradient(transparent 95%, rgba(255,176,42,.14) 100%);
  background-size: 100% 184px; }
.rm-scan { position:absolute; inset:0; pointer-events:none; opacity:.4; mix-blend-mode:overlay;
  background:repeating-linear-gradient(to bottom, rgba(0,0,0,0) 0 2px, rgba(0,0,0,.55) 2px 3px);
  animation: rm-scan 8s linear infinite; }
@keyframes rm-scan { from{ background-position:0 0; } to{ background-position:0 300px; } }
.rm-grain { position:absolute; inset:-50%; pointer-events:none; opacity:.08; mix-blend-mode:overlay;
  background-image:url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='120' height='120' filter='url(%23n)'/></svg>");
  animation: rm-grain 1.6s steps(4) infinite; }
@keyframes rm-grain { 0%{transform:translate(0,0);}25%{transform:translate(-4%,3%);}50%{transform:translate(3%,-2%);}75%{transform:translate(-2%,-3%);}100%{transform:translate(2%,2%);} }
.rm-vignette { position:absolute; inset:0; pointer-events:none;
  background:radial-gradient(120% 100% at 50% 42%, transparent 32%, rgba(2,4,6,.92) 100%); }
.rm-vignette::after { content:''; position:absolute; inset:0; pointer-events:none;
  background: linear-gradient(180deg, rgba(8,12,16,.4) 0%, transparent 18%, transparent 82%, rgba(8,12,16,.5) 100%); }

/* ---------- global corner brackets ----------------------------------- */
.rm-frame { position:absolute; inset:0; pointer-events:none; z-index:2; }
.rm-brk { position:absolute; width:36px; height:36px;
  border:2.5px solid rgba(255,176,42,.9);
  filter: drop-shadow(0 0 10px rgba(255,176,42,.6));
}
.rm-brk::after { content:''; position:absolute; width:5px; height:5px; background:var(--amber);
  box-shadow:0 0 10px var(--amber), 0 0 20px var(--amber); }
.rm-brk-tl { left:18px; top:18px; border-right:0; border-bottom:0; }
.rm-brk-tl::after { right:-2.5px; bottom:-2.5px; }
.rm-brk-tr { right:18px; top:18px; border-left:0; border-bottom:0; }
.rm-brk-tr::after { left:-2.5px; bottom:-2.5px; }
.rm-brk-bl { left:18px; bottom:18px; border-right:0; border-top:0; }
.rm-brk-bl::after { right:-2.5px; top:-2.5px; }
.rm-brk-br { right:18px; bottom:18px; border-left:0; border-top:0; }
.rm-brk-br::after { left:-2.5px; top:-2.5px; }

/* ---------- stage grid ----------------------------------------------- */
.rm-stage {
  position:absolute; inset:0;
  display:grid;
  grid-template-columns: minmax(300px, 26vw) 1fr minmax(230px, 22vw);
  /* The middle row MUST be minmax(0, 1fr), not a bare 1fr: a bare 1fr's
     automatic minimum size is its content's min-content height, so the row
     grows to fit the tallest column (the right rail's stacked panels) instead
     of being capped to the space actually left after the top/bottom bars.
     That silently pushed the bottom status bar past the viewport — invisible
     because .rm-root clips overflow — on perfectly ordinary displays
     (1920x1080, 1440x900), which is exactly what made zooming out "fix" it. */
  grid-template-rows: auto minmax(0, 1fr) auto;
  grid-template-areas:
    "top   top   top"
    "left  hero  right"
    "bot   bot   bot";
  gap: clamp(10px, 1.4vh, 20px);
  padding: clamp(26px, 4vh, 54px) clamp(30px, 4vw, 64px);
}
.rm-top { grid-area:top; display:flex; align-items:center; justify-content:space-between; gap:24px; min-height:0; }
.rm-left { grid-area:left; display:flex; flex-direction:column; justify-content:center; gap:clamp(14px,2.4vh,30px); min-width:0; min-height:0; }
.rm-hero { grid-area:hero; position:relative; display:flex; align-items:center; justify-content:center; min-width:0; min-height:0; }
.rm-right { grid-area:right; display:flex; flex-direction:column; justify-content:center; gap:clamp(6px,1.2vh,18px); min-width:0; min-height:0; }
.rm-bottom { grid-area:bot; display:flex; align-items:stretch; gap:clamp(8px,1vw,16px); min-height:0; }

/* ---------- entrance stagger ----------------------------------------- */
.rm-rise { opacity:0; transform:translateY(14px); }
.rm-live .rm-rise { animation: rm-rise-in .6s cubic-bezier(.2,.7,.2,1) both; animation-delay:var(--rise,0s); }
@keyframes rm-rise-in { from{ opacity:0; transform:translateY(14px);} to{ opacity:1; transform:translateY(0);} }

/* ---------- top: service record -------------------------------------- */
.rm-record { display:flex; align-items:center; gap:14px; padding:12px 20px;
  background:linear-gradient(180deg, rgba(22,30,40,.96), rgba(8,13,17,.96));
  border:1px solid rgba(255,176,42,.5); clip-path:var(--shear);
  -webkit-clip-path:var(--shear); min-width:min(360px,40vw); position:relative;
  box-shadow: 0 8px 30px rgba(0,0,0,.65), 0 0 28px rgba(255,176,42,.18), inset 0 1px 0 rgba(255,176,42,.18); }
.rm-record::before { content:''; position:absolute; left:0; top:8px; bottom:8px; width:4px;
  background:linear-gradient(180deg, transparent 0%, var(--amber) 50%, transparent 100%);
  box-shadow: 0 0 14px rgba(255,176,42,.95), 0 0 28px rgba(255,176,42,.55); }
.rm-record::after { content:''; position:absolute; left:0; right:0; top:0; height:1px;
  background: linear-gradient(90deg, transparent 5%, var(--hair) 30%, var(--hair) 70%, transparent 95%); }
.rm-rank { width:48px; height:48px; flex:0 0 auto; filter:drop-shadow(0 0 14px rgba(255,176,42,.78)); }
.rm-rank svg { width:100%; height:100%; display:block; }
.rm-record-info { flex:1; min-width:0; }
.rm-record-top { display:flex; align-items:baseline; gap:10px; }
.rm-callsign { font-family:var(--fd); font-size:clamp(16px,1.5vw,22px); letter-spacing:.14em; color:#fff;
  text-shadow: 0 0 12px rgba(0,0,0,.85), 0 0 28px rgba(255,176,42,.3);
  background: linear-gradient(180deg, #fff 0%, #c8d8e6 100%);
  -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; }
.rm-clearance { font-size:9px; letter-spacing:.2em; color:var(--amber); border:1px solid rgba(255,176,42,.7);
  padding:2px 7px; box-shadow: 0 0 12px rgba(255,176,42,.4), inset 0 0 8px rgba(255,176,42,.22);
  text-shadow: 0 0 8px rgba(255,176,42,.7); background: rgba(255,176,42,.08); font-weight:700; }
.rm-record-sub { display:flex; align-items:center; gap:8px; margin-top:3px; font-size:9.5px;
  letter-spacing:.16em; color:var(--ink-2); }
.rm-lvl { color:var(--ink); }
.rm-dot { width:3px; height:3px; border-radius:50%; background:var(--ink-3); flex:0 0 auto; }
.rm-xp { position:relative; height:5px; margin-top:8px; background:rgba(5,9,12,.92);
  box-shadow:inset 0 0 0 1px rgba(0,0,0,.6), 0 0 0 1px var(--hair-2); overflow:hidden; }
.rm-xp i { position:absolute; left:0; top:0; bottom:0; background:linear-gradient(90deg,#a86510,#ffd884,var(--amber-3));
  box-shadow:0 0 14px rgba(255,176,42,.9), 0 0 28px rgba(255,176,42,.42); }
.rm-xp i::after { content:''; position:absolute; right:0; top:0; bottom:0; width:14px;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,.85));
  animation: rm-xp-shine 2s ease-in-out infinite; }
@keyframes rm-xp-shine { 0%,100%{ opacity:0; } 50%{ opacity:1; } }
.rm-xp-lbl { display:flex; justify-content:space-between; margin-top:5px; font-size:8.5px;
  letter-spacing:.16em; color:var(--ink-3); }

/* ---------- top: telemetry ------------------------------------------- */
.rm-telemetry { display:flex; align-items:stretch; gap:0; background:linear-gradient(180deg, rgba(18,26,34,.94), rgba(8,13,17,.96));
  border:1px solid rgba(255,176,42,.4); position:relative; overflow:hidden;
  box-shadow: 0 6px 26px rgba(0,0,0,.55), inset 0 1px 0 rgba(255,176,42,.14); }
.rm-telemetry::before { content:''; position:absolute; left:0; right:0; top:0; height:1px;
  background: linear-gradient(90deg, transparent, rgba(255,176,42,.85), transparent); }
.rm-telemetry::after { content:''; position:absolute; left:0; right:0; bottom:0; height:1px;
  background: linear-gradient(90deg, transparent, rgba(121,210,255,.5), transparent); }
.rm-tel-cell { padding:10px 18px; display:flex; flex-direction:column; gap:3px;
  border-left:1px solid var(--hair-2); min-width:84px; position:relative; }
.rm-tel-cell:first-child { border-left:0; }
.rm-tel-k { font-size:8px; letter-spacing:.26em; color:var(--ink-3); }
.rm-tel-v { font-family:var(--fm); font-size:12.5px; letter-spacing:.04em; color:var(--ink);
  text-shadow: 0 0 8px rgba(0,0,0,.6), 0 0 14px rgba(255,255,255,.18); font-weight:600; }
.rm-tel-ok { color:var(--ok); text-shadow: 0 0 10px rgba(168,232,106,.7); }
.rm-mono { font-family:var(--fm); letter-spacing:.02em; }
.rm-amber { color:var(--amber); text-shadow: 0 0 10px rgba(255,176,42,.7); }

/* ---------- left: brand ---------------------------------------------- */
.rm-logo { display:flex; align-items:flex-start; gap:6px; position:relative; }
.rm-logo::before { content:''; position:absolute; left:-30px; top:8%; width:8px; height:84%;
  background:linear-gradient(180deg, var(--amber-3) 0%, var(--amber) 50%, var(--amber-2) 100%);
  box-shadow: 0 0 22px var(--amber), 0 0 44px rgba(255,176,42,.6);
  clip-path: polygon(0 0, 100% 0, 100% 70%, 50% 100%, 0 70%); }
.rm-logo-main { font-family:var(--fd); font-weight:800; font-size:clamp(68px,8.4vw,136px);
  line-height:.8; letter-spacing:.06em; color:#fff;
  background: linear-gradient(180deg, #ffffff 0%, #ffffff 44%, #ffc251 84%, #ffb02a 100%);
  -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
  text-shadow: 0 0 50px rgba(255,176,42,.45);
  filter: drop-shadow(0 4px 28px rgba(0,0,0,.95)) drop-shadow(0 0 50px rgba(255,176,42,.6));
  position: relative; }
.rm-logo-main::after { content:'RIFT'; position:absolute; inset:0;
  background: linear-gradient(180deg, transparent 0%, transparent 82%, rgba(255,176,42,.85) 100%);
  -webkit-background-clip: text; background-clip: text;
  -webkit-text-fill-color: transparent; pointer-events:none; }
.rm-logo-tag { font-family:var(--fd); font-size:clamp(22px,2.3vw,36px); color:var(--amber-3);
  margin-top:.4em; letter-spacing:.1em; font-weight:700;
  text-shadow: 0 0 20px rgba(255,176,42,.85), 0 0 40px rgba(255,176,42,.42), 0 2px 6px rgba(0,0,0,.7); }
.rm-tagline { margin-top:8px; font-size:clamp(10px,1vw,13px); letter-spacing:.36em; color:var(--ink-2);
  text-shadow: 0 1px 0 rgba(0,0,0,.8); font-weight:600; }

/* logo glitch — one-shot at boot. Adds a brief chromatic-aberration
   flicker that says "system coming online". */
.rm-live .rm-logo-main { animation: rm-logo-glitch 1.4s steps(8) both; }
@keyframes rm-logo-glitch {
  0%   { transform: translateX(0) skewX(0); filter: drop-shadow(0 4px 28px rgba(0,0,0,.95)) drop-shadow(0 0 50px rgba(255,176,42,.6)); }
  18%  { transform: translateX(-3px) skewX(-2deg); filter: drop-shadow(-4px 0 0 rgba(255,63,49,.7)) drop-shadow(4px 0 0 rgba(121,210,255,.6)) drop-shadow(0 4px 28px rgba(0,0,0,.95)); }
  30%  { transform: translateX(2px) skewX(1.5deg); filter: drop-shadow(3px 0 0 rgba(255,63,49,.6)) drop-shadow(-3px 0 0 rgba(121,210,255,.7)); }
  44%  { transform: translateX(-1px) skewX(-1deg); filter: drop-shadow(-2px 0 0 rgba(255,63,49,.4)) drop-shadow(2px 0 0 rgba(121,210,255,.5)); }
  60%  { transform: translateX(0) skewX(0); filter: drop-shadow(0 0 0 transparent) drop-shadow(0 4px 28px rgba(0,0,0,.95)); }
  100% { transform: translateX(0) skewX(0); filter: drop-shadow(0 4px 28px rgba(0,0,0,.95)) drop-shadow(0 0 50px rgba(255,176,42,.6)); }
}
@media (prefers-reduced-motion: reduce) {
  .rm-live .rm-logo-main { animation: none; }
}
.rm-brand-meta { display:flex; align-items:center; gap:10px; margin-top:14px;
  font-size:9px; letter-spacing:.26em; color:var(--ink-2);
  padding-top:12px; border-top:1px solid rgba(255,176,42,.3); }
.rm-brand-meta > span:first-child {
  color:var(--amber); text-shadow: 0 0 12px rgba(255,176,42,.78);
  display:inline-flex; align-items:center; gap:6px; font-weight:700; }
.rm-brand-meta > span:first-child::before { content:''; width:6px; height:6px; background:var(--amber);
  border-radius:50%; box-shadow:0 0 10px var(--amber), 0 0 22px var(--amber); animation: rm-pulse 1.6s ease-in-out infinite; }
@keyframes rm-pulse { 0%,100%{ opacity:1; transform:scale(1); } 50%{ opacity:.4; transform:scale(1.4); } }

/* ---------- left: nav rail ------------------------------------------- */
.rm-nav { display:flex; flex-direction:column; gap:4px; border-left:2px solid rgba(255,176,42,.5);
  padding-left:2px; }
.rm-navitem {
  position:relative; appearance:none; border:0; background:transparent; cursor:pointer;
  display:flex; align-items:center; gap:14px; width:100%; text-align:left;
  padding:clamp(10px,1.4vh,16px) 16px; color:var(--ink-2);
  font-family:var(--ff); font-weight:700; text-transform:uppercase;
  transition: background .16s ease, color .16s ease, padding-left .18s cubic-bezier(.2,.7,.2,1);
  outline:none;
}
.rm-navitem::before { content:''; position:absolute; left:0; right:0; top:0; bottom:0;
  background:linear-gradient(90deg, rgba(255,176,42,.32) 0%, rgba(255,176,42,.08) 60%, transparent 100%);
  opacity:0; transform:translateX(-8px); transition:opacity .2s ease, transform .22s cubic-bezier(.2,.7,.2,1); pointer-events:none; }
.rm-navitem::after { content:''; position:absolute; left:0; top:0; bottom:0; width:0;
  background:linear-gradient(90deg, rgba(255,176,42,.22), transparent 80%); transition:width .2s ease; }
.rm-carat { position:absolute; left:-3px; top:50%; width:4px; height:0; background:var(--amber);
  transform:translateY(-50%); box-shadow:0 0 18px var(--amber), 0 0 36px rgba(255,176,42,.6);
  transition:height .22s cubic-bezier(.2,.7,.2,1); }
.rm-nav-body { display:flex; flex-direction:column; gap:2px; z-index:1; }
.rm-nav-label { font-size:clamp(15px,1.6vw,22px); letter-spacing:.16em; line-height:1;
  text-shadow: 0 1px 2px rgba(0,0,0,.8); }
.rm-nav-sub { font-size:8.5px; letter-spacing:.2em; color:var(--ink-3); opacity:.9; }
.rm-nav-idx { margin-left:auto; font-family:var(--fm); font-size:10px; color:var(--ink-3); z-index:1;
  transition:color .16s ease; }
.rm-navitem:hover { color:#fff; padding-left:24px; }
.rm-navitem:hover::before { opacity:1; transform:translateX(0); }
.rm-navitem.rm-on { color:#fff; padding-left:28px; }
.rm-navitem.rm-on::after { width:100%; }
.rm-navitem.rm-on::before { opacity:1; transform:translateX(0); }
.rm-navitem.rm-on .rm-carat { height:78%; }
.rm-navitem.rm-on .rm-nav-idx { color:var(--amber); }
.rm-navitem:focus-visible { box-shadow:inset 0 0 0 1px rgba(255,176,42,.7); }
.rm-navitem.rm-primary .rm-nav-label { color:#fff; }
.rm-navitem.rm-primary.rm-on { background:linear-gradient(90deg, rgba(255,176,42,.35), rgba(255,176,42,.05));
  box-shadow: inset 0 0 0 1px rgba(255,176,42,.4); }
.rm-navitem.rm-primary.rm-on .rm-nav-label {
  text-shadow:0 0 18px rgba(255,176,42,.8), 0 0 36px rgba(255,176,42,.4); }
.rm-navitem.rm-primary::after { content:''; position:absolute; right:14px; top:50%; transform:translateY(-50%) rotate(45deg);
  width:8px; height:8px; border-top:2px solid var(--amber); border-right:2px solid var(--amber);
  opacity:0; transition:opacity .16s ease, right .18s ease; }
.rm-navitem.rm-primary.rm-on::after, .rm-navitem.rm-primary:hover::after { opacity:1; right:10px; }
.rm-navitem:active { transform:translateY(1px); }

.rm-hint { display:flex; align-items:center; gap:8px; font-size:9px; letter-spacing:.18em; color:var(--ink-3);
  padding:8px 0; margin-top:6px; border-top:1px solid var(--hair-2); }
.rm-hint-t { margin-right:6px; }
.rm-key { display:inline-flex; align-items:center; justify-content:center; min-width:18px; height:18px;
  padding:0 5px; border:1px solid var(--hair); background:rgba(8,12,16,.6); border-radius:2px;
  font-size:9px; color:var(--ink-2); }

/* ---------- hero: armillary PLAY dial --------------------------------- */
.rm-hero { overflow:visible; }
.rm-dial { position:relative; width:min(50vw,68vh,640px); aspect-ratio:1/1;
  container-type:inline-size; container-name:rm-dial;
  filter:drop-shadow(0 24px 60px rgba(0,0,0,.7)); z-index:2; }
.rm-dial::before { content:''; position:absolute; inset:8%; border-radius:50%;
  background: radial-gradient(circle, rgba(255,176,42,.4) 0%, rgba(255,176,42,.12) 40%, transparent 70%);
  filter: blur(28px); animation: rm-dial-breathe 6s ease-in-out infinite; pointer-events:none; }
.rm-dial-bezel { position:absolute; left:50%; top:50%; width:min(60vw,82vh,780px); aspect-ratio:1/1;
  transform:translate(-50%,-50%); pointer-events:none; z-index:1; filter:drop-shadow(0 8px 24px rgba(0,0,0,.45)); }
.rm-bezel-svg { position:absolute; inset:0; width:100%; height:100%; display:block; }
.rm-bezel-rot { animation: rm-bezel-rot 120s linear infinite; transform-origin:center; transform-box:fill-box; }
@keyframes rm-bezel-rot { to { transform: rotate(360deg); } }
.rm-bezel-arc-a { animation: rm-bezel-arc 22s linear infinite; }
.rm-bezel-arc-b { animation: rm-bezel-arc 30s linear infinite reverse; }
@keyframes rm-bezel-arc { to { stroke-dashoffset: -1920; } }
.rm-dial-bolt { position:absolute; width:10px; height:10px; border-radius:50%;
  background: radial-gradient(circle at 35% 35%, #ffd884 0%, #ffb02a 55%, #6e3d04 100%);
  box-shadow: 0 0 8px rgba(255,176,42,.85), inset 0 1px 0 rgba(255,255,255,.55), inset 0 -1px 0 rgba(0,0,0,.45);
  z-index:3; }
.rm-dial-bolt::after { content:''; position:absolute; inset:2px; border-radius:50%;
  border-top:1px solid rgba(0,0,0,.6); transform: rotate(35deg); }
.rm-dial-bolt-tl { left:8%; top:8%; }
.rm-dial-bolt-tr { right:8%; top:8%; }
.rm-dial-bolt-bl { left:8%; bottom:8%; }
.rm-dial-bolt-br { right:8%; bottom:8%; }
.rm-dial-svg { position:absolute; inset:0; width:100%; height:100%; display:block;
  animation: rm-dial-breathe 6s ease-in-out infinite; }
@keyframes rm-dial-breathe { 0%,100%{ filter:brightness(1);} 50%{ filter:brightness(1.18);} }
.rm-ring { transform-box:fill-box; transform-origin:center; }
.rm-ring-a { animation: rm-dial-spin 26s linear infinite; }
.rm-ring-b { animation: rm-dial-spin 34s linear infinite reverse; }
.rm-ring-c { animation: rm-dial-spin 20s linear infinite; }
@keyframes rm-dial-spin { from{ transform:rotate(0);} to{ transform:rotate(360deg);} }
.rm-dial-play { position:absolute; left:50%; top:50%; width:40%; height:40%; transform:translate(-50%,-50%);
  border-radius:50%; border:2.5px solid rgba(255,176,42,.98); background:radial-gradient(circle at 50% 35%, rgba(80,50,12,.9) 0%, rgba(20,12,4,.96) 65%);
  cursor:pointer; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:6px;
  transition:box-shadow .22s ease, transform .22s cubic-bezier(.2,.7,.2,1), border-color .2s ease;
  box-shadow: 0 0 0 1px rgba(255,176,42,.5), 0 0 50px rgba(255,176,42,.55), 0 0 100px rgba(255,176,42,.3), inset 0 0 50px rgba(255,176,42,.32);
  overflow:hidden; }
.rm-dial-play::before { content:''; position:absolute; inset:6%; border-radius:50%;
  background: conic-gradient(from 0deg, transparent 0deg, rgba(255,176,42,.5) 30deg, transparent 60deg, transparent 360deg);
  animation: rm-dial-spin 8s linear infinite; opacity:.95; }
.rm-dial-play::after { content:''; position:absolute; inset:18%; border-radius:50%;
  background: linear-gradient(180deg, transparent 0%, rgba(255,176,42,.22) 50%, transparent 100%);
  animation: rm-dial-pulse 3s ease-in-out infinite; }
@keyframes rm-dial-pulse { 0%,100%{ opacity:.4; transform:scale(1); } 50%{ opacity:1; transform:scale(1.05); } }
.rm-dial-play-eyebrow { position:relative; z-index:1; font-family:var(--fm); font-size:9px; letter-spacing:.32em; color:var(--amber);
  text-shadow:0 0 10px rgba(255,176,42,.85), 0 0 20px rgba(255,176,42,.42); }
.rm-dial-play-label { position:relative; z-index:1; color:#fff; font-family:var(--fd);
  /* Sized off the dial's own container, not the viewport: the dial itself
     shrinks on short screens (see the height media queries below), and a
     vw-based font doesn't know that — it kept rendering at ~full size while
     the circle it sits in shrank, so "PLAY" overflowed past the ring. */
  font-size:clamp(16px, 6.9cqw, 44px);
  font-weight:800; letter-spacing:.26em; text-shadow: 0 0 18px rgba(255,176,42,.7), 0 2px 10px rgba(0,0,0,.9), 0 0 36px rgba(255,176,42,.3);
  transition:letter-spacing .2s ease, color .2s ease, text-shadow .2s ease; }
.rm-dial-play:hover, .rm-dial-play:focus-visible { outline:0; transform: translate(-50%,-50%) scale(1.05);
  box-shadow: 0 0 0 14px rgba(255,176,42,.14), 0 0 100px rgba(255,176,42,.78), inset 0 0 60px rgba(255,176,42,.45);
  border-color: var(--amber); }
.rm-dial-play:hover .rm-dial-play-label, .rm-dial-play:focus-visible .rm-dial-play-label {
  color:var(--amber-3); letter-spacing:.36em;
  text-shadow:0 0 26px rgba(255,176,42,.95), 0 0 52px rgba(255,176,42,.55), 0 2px 10px rgba(0,0,0,.9); }
.rm-dial-play:hover .rm-dial-play-eyebrow { color:var(--amber-3); }
.rm-op-rim { filter:drop-shadow(0 0 16px rgba(255,176,42,.85)); }

/* ---------- right panels --------------------------------------------- */
.rm-panel { background:linear-gradient(180deg, rgba(22,30,40,.96), rgba(8,13,17,.98)); border:1px solid rgba(255,176,42,.5);
  padding:clamp(10px,1.8vh,16px) clamp(12px,1.6vw,18px); min-height:0; flex-shrink:1;
  clip-path:var(--shear); -webkit-clip-path:var(--shear); position:relative;
  box-shadow: inset 0 1px 0 rgba(255,176,42,.18), 0 6px 28px rgba(0,0,0,.6), 0 0 28px rgba(255,176,42,.1); }
.rm-panel::before { content:''; position:absolute; left:0; top:14px; width:4px; bottom:14px;
  background:linear-gradient(180deg, transparent 0%, var(--amber) 50%, transparent 100%);
  box-shadow: 0 0 14px rgba(255,176,42,.95), 0 0 28px rgba(255,176,42,.45); opacity:.98; }
.rm-panel::after { content:''; position:absolute; left:0; right:0; top:0; height:1px;
  background: linear-gradient(90deg, transparent 5%, var(--hair) 30%, var(--hair) 70%, transparent 95%); }
.rm-panel-head { display:flex; align-items:baseline; justify-content:space-between; gap:8px;
  padding-bottom:clamp(6px,1vh,10px); margin-bottom:clamp(6px,1.2vh,12px); border-bottom:1px solid rgba(255,176,42,.3); position:relative; }
.rm-panel-title { font-family:var(--fd); font-size:17px; letter-spacing:.22em; color:#fff; font-weight:800;
  text-shadow: 0 0 12px rgba(0,0,0,.7), 0 0 20px rgba(255,176,42,.2); }
.rm-panel-sub { font-size:9px; letter-spacing:.16em; color:var(--amber);
  text-shadow: 0 0 10px rgba(255,176,42,.6); transition:color .2s ease; }
.rm-panel-sub.rm-flash { color:var(--cyan); text-shadow: 0 0 12px rgba(121,210,255,.78); }
.rm-panel-head-main { display:flex; align-items:baseline; gap:8px; min-width:0; }
.rm-op-switch { display:flex; gap:4px; flex:0 0 auto; }
.rm-op-arrow { appearance:none; cursor:pointer; border:1px solid rgba(255,176,42,.4); background:rgba(4,9,13,.75); color:var(--ink-2);
  width:26px; height:26px; padding:0; display:flex; align-items:center; justify-content:center; font-size:13px; line-height:1;
  transition: all .16s ease; }
.rm-op-arrow:hover, .rm-op-arrow:focus-visible { color:var(--amber); border-color:rgba(255,176,42,.85);
  box-shadow: 0 0 14px rgba(255,176,42,.45); outline:0; background:rgba(20,12,4,.85); }

.rm-op-media { display:grid; grid-template-columns:1.15fr 1fr; gap:8px; margin-bottom:clamp(6px,1vh,10px); }
.rm-op-portrait { position:relative; aspect-ratio:3/4; max-height:clamp(120px, 20vh, 230px); overflow:hidden; border:1px solid rgba(255,176,42,.4);
  background:
    linear-gradient(180deg, transparent 0%, transparent 60%, rgba(0,0,0,.55) 100%),
    linear-gradient(160deg, rgba(24,32,40,.92), rgba(6,9,13,.98));
  box-shadow: inset 0 0 0 1px rgba(255,176,42,.1), inset 0 0 28px rgba(0,0,0,.55), 0 0 14px rgba(255,176,42,.1); }
.rm-op-portrait::before { content:''; position:absolute; left:0; right:0; top:0; height:18px;
  background: linear-gradient(180deg, rgba(255,176,42,.22), transparent);
  z-index:2; pointer-events:none; }
.rm-op-portrait::after { content:''; position:absolute; inset:0; pointer-events:none; z-index:2;
  background: linear-gradient(180deg, transparent 0%, transparent 50%, rgba(0,0,0,.5) 100%); }
.rm-op-portrait-art { position:absolute; inset:0; transition:opacity .5s ease, filter .5s ease; }
.rm-op-portrait-art svg { position:absolute; inset:0; width:100%; height:100%; display:block; }
.rm-op-portrait-canvas { position:absolute; inset:0; display:flex; align-items:flex-end; justify-content:center; pointer-events:none; }

/* scan-line overlay — slides down the portrait on operator switch */
.rm-op-scan { position:absolute; left:0; right:0; top:-2px; height:2px; z-index:3;
  background: linear-gradient(90deg, transparent, rgba(255,176,42,.0) 10%, rgba(255,176,42,.85) 50%, rgba(255,176,42,.0) 90%, transparent);
  box-shadow: 0 0 12px rgba(255,176,42,.95), 0 0 24px rgba(255,176,42,.55);
  pointer-events:none; opacity:0; }
.rm-op-flash .rm-op-scan { animation: rm-op-scan-sweep .9s cubic-bezier(.4,.0,.2,1) both; }
@keyframes rm-op-scan-sweep {
  0%   { transform: translateY(0); opacity:0; }
  10%  { opacity:1; }
  100% { transform: translateY(420px); opacity:0; }
}
/* faint crosshair that sits centered over the portrait */
.rm-op-crosshair { position:absolute; left:50%; top:50%; width:62%; height:62%;
  transform:translate(-50%,-50%); z-index:2; pointer-events:none; opacity:.28;
  background:
    linear-gradient(90deg, transparent 49.7%, rgba(255,176,42,.6) 49.7%, rgba(255,176,42,.6) 50.3%, transparent 50.3%),
    linear-gradient(0deg, transparent 49.7%, rgba(255,176,42,.6) 49.7%, rgba(255,176,42,.6) 50.3%, transparent 50.3%),
    radial-gradient(circle at center, transparent 28%, rgba(255,176,42,.4) 28.5%, rgba(255,176,42,.4) 29.5%, transparent 30%);
  transition: opacity .4s ease; }
.rm-op-flash .rm-op-crosshair { opacity:.6; }
@media (prefers-reduced-motion: reduce) {
  .rm-op-flash .rm-op-scan { animation: none; }
}
.rm-3d { height:100%; width:auto; opacity:0; transition:opacity .7s ease; filter:drop-shadow(0 10px 20px rgba(0,0,0,.6)); }
.rm-op-portrait.rm-3d-live .rm-3d { opacity:1; }
.rm-op-portrait.rm-3d-live .rm-op-portrait-art { opacity:.16; filter:blur(2px); }
.rm-op-media-side { display:grid; grid-template-rows:1fr 1fr; gap:8px; }
.rm-op-swatch { position:relative; overflow:hidden; border:1px solid var(--hair); display:flex; align-items:flex-end; padding:6px; }
.rm-op-swatch-a { background:
    repeating-linear-gradient(115deg, rgba(255,255,255,.04) 0 6px, transparent 6px 14px),
    linear-gradient(150deg, #2d3a44, #10151b); }
.rm-op-swatch-b { background:
    radial-gradient(120% 90% at 70% 20%, rgba(255,176,42,.3), transparent 60%),
    linear-gradient(160deg, #1c2126, #0a0d10); }
.rm-op-swatch-label { font-size:8px; letter-spacing:.2em; color:var(--ink-2);
  text-shadow: 0 0 4px rgba(0,0,0,.8); }

.rm-op-tabs { display:flex; gap:6px; margin-bottom:clamp(6px,1vh,10px); }
.rm-op-tab { flex:1; appearance:none; cursor:pointer; border:1px solid var(--hair); background:rgba(4,9,13,.5);
  color:var(--ink-2); padding:7px 4px; font-family:var(--ff); font-size:9px; font-weight:700; letter-spacing:.14em;
  transition: all .15s ease; }
.rm-op-tab:hover { color:#fff; border-color:rgba(255,176,42,.4); }
.rm-op-tab.rm-on { color:#fff; border-color:rgba(255,176,42,.7); background:linear-gradient(180deg, rgba(255,176,42,.18), rgba(255,176,42,.05));
  box-shadow: 0 0 12px rgba(255,176,42,.25), inset 0 1px 0 rgba(255,176,42,.3); }
.rm-op-locked .rm-op-portrait-art { filter:grayscale(.7) brightness(.55); }
.rm-op-locked .rm-file-status .rm-file-v { color:var(--red); }

/* On a short viewport the panel itself gets flex-shrunk (see .rm-panel above)
   below its content's natural height. Without this, the bio/kit/history rows
   just spilled out past the panel's own border instead of the file list
   growing a scrollbar — header, portrait and tabs stay full size, the row
   list is what gives. */
.rm-readout { display:flex; flex-direction:column; min-height:0; }
.rm-readout .rm-panel-head, .rm-readout .rm-op-media, .rm-readout .rm-op-tabs { flex:0 0 auto; }

.rm-squadtop { display:flex; align-items:center; gap:10px; padding:10px 16px; background:linear-gradient(180deg, rgba(16,24,32,.9), rgba(8,13,17,.95));
  border:1px solid rgba(255,176,42,.3); flex:0 0 auto; position:relative; overflow:hidden;
  box-shadow: 0 0 16px rgba(0,0,0,.4), inset 0 1px 0 rgba(255,176,42,.1); }
.rm-squadtop::before { content:''; position:absolute; left:-100%; top:0; bottom:0; width:50%;
  background: linear-gradient(90deg, transparent, rgba(255,176,42,.08), transparent);
  animation: rm-squadtop-sweep 6s linear infinite; pointer-events:none; }
@keyframes rm-squadtop-sweep { 0%{ transform:translateX(0); } 100%{ transform:translateX(400%); } }
.rm-squadtop .rm-avatars { display:flex; }
.rm-squadtop .rm-avatars i { width:18px; height:18px; border:1px solid var(--hair); background:#1a2732;
  transform:rotate(45deg); margin-left:-6px; transition: all .2s ease; }
.rm-squadtop .rm-avatars i:hover { border-color: var(--amber); box-shadow: 0 0 8px rgba(255,176,42,.5); }
.rm-squadtop .rm-avatars i.me { background:linear-gradient(135deg,#5a7a92,#16222c); border-color:rgba(255,176,42,.7); margin-left:0;
  box-shadow: 0 0 10px rgba(255,176,42,.4); }
.rm-squadtop .rm-mono { font-size:11px; color:var(--ink); }

.rm-map { position:relative; width:100%; aspect-ratio:24/17; max-height:clamp(140px, 22vh, 220px);
  border:1px solid var(--hair-2); overflow:hidden;
  box-shadow: inset 0 0 24px rgba(0,0,0,.5); }
.rm-map::before { content:''; position:absolute; inset:0; pointer-events:none;
  background: linear-gradient(180deg, rgba(8,12,16,.4) 0%, transparent 18%, transparent 82%, rgba(8,12,16,.4) 100%); z-index:2; }
.rm-map svg { position:absolute; inset:0; width:100%; height:100%; display:block; }
.rm-map-ping { transform-origin:center; transform-box:fill-box; animation:rm-ping 2.4s ease-out infinite; }
@keyframes rm-ping { 0%{ transform:scale(.4); opacity:1;} 70%{ transform:scale(1.6); opacity:0;} 100%{ opacity:0;} }
.rm-map-legend { display:flex; gap:14px; margin-top:8px; font-size:8.5px; letter-spacing:.14em; color:var(--ink-2); }
.rm-lg { display:flex; align-items:center; gap:6px; }
.rm-lg i { width:7px; height:7px; box-shadow: 0 0 6px currentColor; }
.rm-lg-us { background:var(--cyan); color: var(--cyan); }
.rm-lg-them { background:var(--red); color: var(--red); clip-path:polygon(50% 0,100% 100%,0 100%); }
.rm-lg-obj { background:var(--amber); color: var(--amber); }

.rm-file { display:flex; flex-direction:column; gap:clamp(3px,0.6vh,6px); flex:1 1 auto; min-height:0; overflow-y:auto; }
.rm-file-row { display:flex; align-items:center; justify-content:space-between; gap:12px;
  font-size:10px; letter-spacing:.12em; padding-bottom:clamp(3px,0.6vh,6px); border-bottom:1px solid var(--hair-2); }
.rm-file-row:last-child { border-bottom:0; }
.rm-file-k { color:var(--ink-3); }
.rm-file-v { color:var(--ink); font-family:var(--fm); }

/* war room — between deployment and operator file. Animated rows + a
   rotating live-feed line so the right column never looks frozen. */
.rm-war-rows { display:flex; flex-direction:column; gap:clamp(2px,0.6vh,6px); margin-bottom:clamp(5px,1vh,10px); }
.rm-war-row { display:flex; align-items:center; justify-content:space-between; gap:10px;
  font-size:9.5px; letter-spacing:.16em; padding:clamp(3px,0.6vh,5px) 0; border-bottom:1px dashed rgba(190,214,230,.14); }
.rm-war-row:last-child { border-bottom:0; }
.rm-war-k { color:var(--ink-3); }
.rm-war-v { color:#fff; font-family:var(--fm); font-weight:700;
  text-shadow: 0 0 6px rgba(0,0,0,.6); }
.rm-war-amber { color:var(--amber); text-shadow: 0 0 8px rgba(255,176,42,.7); }
.rm-war-ok { color:var(--ok); text-shadow: 0 0 8px rgba(168,232,106,.6); }
.rm-war-cyan { color:var(--cyan); text-shadow: 0 0 8px rgba(121,210,255,.7); }
.rm-war-bar { display:flex; align-items:center; gap:8px; margin-top:2px; padding:6px 8px;
  background:linear-gradient(90deg, rgba(4,7,11,.7), rgba(8,12,16,.5));
  border:1px solid rgba(190,214,230,.14); }
.rm-war-bar-k { font-size:8.5px; letter-spacing:.16em; color:var(--ink-3); flex:0 0 auto; }
.rm-war-bar-track { flex:1; min-width:60px; height:6px; }
.rm-war-bar-v { font-family:var(--fm); font-size:9.5px; color:#fff; min-width:34px; text-align:right;
  text-shadow: 0 0 6px rgba(255,176,42,.5); }
.rm-war-live { display:flex; align-items:center; gap:8px; margin-top:clamp(5px,1vh,10px); padding-top:clamp(5px,1vh,8px);
  border-top:1px solid rgba(190,214,230,.14); font-size:9px; letter-spacing:.14em; color:var(--ink-2); }
.rm-war-live-dot { width:6px; height:6px; background:var(--ok); border-radius:50%;
  box-shadow: 0 0 10px var(--ok), 0 0 18px rgba(168,232,106,.5);
  animation: rm-pulse 1.4s ease-in-out infinite; }
.rm-war-live-tag { color:var(--ok); font-weight:800; letter-spacing:.22em;
  text-shadow: 0 0 6px rgba(168,232,106,.6); }
.rm-war-live-text { font-family:var(--fm); font-size:9px; letter-spacing:.06em;
  white-space:nowrap; overflow:hidden; text-overflow:ellipsis; min-width:0; flex:1; }

/* ---------- bottom status bar ---------------------------------------- */
.rm-status-cell { display:flex; align-items:center; gap:10px; padding:11px 20px;
  background:linear-gradient(180deg, rgba(18,26,34,.94), rgba(8,13,17,.96)); border:1px solid rgba(255,176,42,.4); position:relative; overflow:hidden;
  box-shadow: 0 4px 22px rgba(0,0,0,.55), inset 0 1px 0 rgba(255,176,42,.14); }
.rm-status-cell::before { content:''; position:absolute; left:0; top:0; bottom:0; width:4px;
  background: linear-gradient(180deg, transparent, var(--cyan), transparent); box-shadow: 0 0 12px var(--cyan), 0 0 24px rgba(121,210,255,.45); }
.rm-status-cell::after { content:''; position:absolute; inset:0; pointer-events:none;
  background: linear-gradient(90deg, rgba(8,12,16,.4) 0%, transparent 12%, transparent 88%, rgba(8,12,16,.4) 100%); }
.rm-tag { font-size:8px; letter-spacing:.24em; color:var(--amber);
  border-right:1px solid rgba(255,176,42,.25); padding-right:10px; flex:0 0 auto;
  text-shadow: 0 0 8px rgba(255,176,42,.5); position:relative; z-index:1; }
.rm-status-cell .rm-mono { font-size:11px; color:var(--ink); position:relative; z-index:1; }
.rm-news { flex:1; min-width:0; overflow:hidden; position:relative; z-index:1; }
.rm-ticker { position:relative; flex:1; overflow:hidden;
  -webkit-mask-image:linear-gradient(90deg,transparent,#000 6%,#000 94%,transparent);
          mask-image:linear-gradient(90deg,transparent,#000 6%,#000 94%,transparent); }
.rm-ticker-line { display:inline-flex; align-items:center; white-space:nowrap;
  animation: rm-marquee 34s linear infinite; }
.rm-ticker-i { font-size:10px; letter-spacing:.16em; color:var(--ink-2); }
.rm-ticker-sep { color:var(--amber); margin:0 14px; text-shadow: 0 0 8px rgba(255,176,42,.6); }
@keyframes rm-marquee { from{ transform:translateX(0);} to{ transform:translateX(-50%);} }

/* ---------- deployment flow ----------------------------------------- */
.rm-flow { position:absolute; z-index:8; left:clamp(30px,7vw,132px); top:50%; width:min(640px,44vw);
  transform:translateY(-46%); opacity:0; visibility:hidden; pointer-events:none;
  transition:opacity .22s ease, transform .26s cubic-bezier(.2,.7,.2,1), visibility .22s; }
.rm-flow::before { content:''; position:absolute; inset:-32px -40px; z-index:-1; pointer-events:none;
  background:linear-gradient(90deg,rgba(6,10,14,.99) 0%,rgba(6,10,14,.94) 70%,transparent 100%);
  border-left:2px solid rgba(255,176,42,.45);
  box-shadow: -20px 0 60px rgba(0,0,0,.6); }
.rm-flow::after { content:''; position:absolute; left:0; top:50%; width:2px; height:0;
  background: linear-gradient(180deg, transparent, var(--amber), transparent);
  transform: translateY(-50%); box-shadow: 0 0 12px var(--amber); transition: height .4s ease; }
.rm-flow-show::after { height:60%; }
.rm-flow-show { opacity:1; visibility:visible; pointer-events:auto; transform:translateY(-50%); }
.rm-flow-eyebrow { color:var(--amber); font-size:10px; letter-spacing:.32em; font-weight:700; margin-bottom:14px;
  text-shadow: 0 0 10px rgba(255,176,42,.5); display:flex; align-items:center; gap:10px; }
.rm-flow-eyebrow::before { content:''; width:24px; height:1px; background: linear-gradient(90deg, transparent, var(--amber)); }
.rm-flow-title { color:#fff; font-family:var(--fd); font-size:clamp(32px,3.2vw,52px); letter-spacing:.05em; line-height:.96;
  text-shadow:0 4px 24px rgba(0,0,0,.9), 0 0 40px rgba(255,176,42,.15);
  background: linear-gradient(180deg, #fff 0%, #d8e6f0 100%);
  -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; }
.rm-flow-copy { max-width:560px; margin:14px 0 22px; color:var(--ink-2); font-size:12px; line-height:1.6; letter-spacing:.065em; }
.rm-flow-choices { display:flex; flex-direction:column; gap:8px; }
.rm-flow-choice { width:100%; position:relative; display:grid; grid-template-columns:minmax(112px,auto) minmax(0,1fr) auto 18px; gap:16px;
  align-items:center; overflow:hidden; appearance:none; cursor:pointer; text-align:left; padding:16px 18px;
  background:linear-gradient(90deg,rgba(17,27,36,.96),rgba(10,15,21,.85)); color:var(--ink); border:1px solid var(--hair);
  border-left:3px solid rgba(159,181,196,.28); clip-path:polygon(0 0,calc(100% - 13px) 0,100% 13px,100% 100%,0 100%);
  font-family:var(--ff); transition:background .18s ease,border-color .18s ease,transform .18s ease,box-shadow .18s ease;
  box-shadow: 0 4px 16px rgba(0,0,0,.3); }
.rm-flow-choice::before { content:''; position:absolute; inset:0; width:0; opacity:.2; transition:width .22s ease; background:linear-gradient(90deg,currentColor,transparent); }
.rm-flow-choice::after { content:''; position:absolute; right:0; top:0; bottom:0; width:0; transition:width .22s ease;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,.06)); }
.rm-flow-choice:hover,.rm-flow-choice:focus-visible { outline:0; transform:translateX(6px);
  background:linear-gradient(90deg,rgba(31,48,61,.99),rgba(12,18,24,.92)); box-shadow: 0 8px 28px rgba(0,0,0,.5); }
.rm-flow-choice:hover::before,.rm-flow-choice:focus-visible::before { width:100%; }
.rm-flow-choice:hover::after,.rm-flow-choice:focus-visible::after { width:50%; }
.rm-flow-choice:active { transform:translateX(3px) scale(.995); }
.rm-flow-choice.rm-flow-pending { opacity:.7; cursor:progress; }
.rm-flow-choice.rm-flow-pending:hover { transform:none; box-shadow:none; }
.rm-flow-choice-lead,.rm-flow-choice-text,.rm-flow-choice-meta,.rm-flow-choice-arrow { position:relative; z-index:1; }
.rm-flow-choice-label { display:block; color:#fff; font-family:var(--fd); font-size:clamp(24px,2.2vw,36px); letter-spacing:.07em; line-height:.9;
  text-shadow: 0 2px 8px rgba(0,0,0,.6); }
.rm-flow-choice-eyebrow { display:block; color:#fff; font-size:11px; font-weight:700; letter-spacing:.2em; }
.rm-flow-choice-copy { display:block; margin-top:5px; color:var(--ink-2); font-size:10.5px; letter-spacing:.055em; line-height:1.45; }
.rm-flow-choice-meta { color:var(--ink-3); font-family:var(--fm); font-size:9px; letter-spacing:.12em; white-space:nowrap; }
.rm-flow-choice-arrow { color:var(--ink-2); font-size:22px; line-height:1; transition:transform .2s ease, color .2s ease; }
.rm-flow-choice:hover .rm-flow-choice-arrow,.rm-flow-choice:focus-visible .rm-flow-choice-arrow { transform:translateX(6px); color:#fff; }
.rm-flow-cyan { color:var(--cyan); border-left-color:rgba(111,208,255,.85); }
.rm-flow-cyan:hover,.rm-flow-cyan:focus-visible { border-color:rgba(111,208,255,.7); box-shadow:0 0 28px rgba(70,159,208,.3), 0 8px 28px rgba(0,0,0,.5); }
.rm-flow-amber { color:var(--amber); border-left-color:rgba(255,176,42,.85); }
.rm-flow-amber:hover,.rm-flow-amber:focus-visible { border-color:rgba(255,176,42,.7); box-shadow:0 0 28px rgba(218,144,25,.3), 0 8px 28px rgba(0,0,0,.5); }
.rm-flow-footer { display:flex; align-items:center; justify-content:space-between; margin-top:18px; padding-top:14px; border-top:1px solid var(--hair); }
.rm-flow-back { appearance:none; cursor:pointer; padding:6px 0; border:0; background:transparent; color:var(--ink-2); font-family:var(--ff);
  font-size:10px; font-weight:700; letter-spacing:.16em; text-transform:uppercase; }
.rm-flow-back::before { content:'←'; margin-right:8px; color:var(--amber); transition:margin-right .18s ease; }
.rm-flow-back:hover,.rm-flow-back:focus-visible { color:#fff; outline:0; }
.rm-flow-back:hover::before { margin-right:12px; }
.rm-flow-step { color:var(--ink-3); font-family:var(--fm); font-size:10px; letter-spacing:.13em; }
.rm-flow-open .rm-left > :not(.rm-brand) { opacity:.16; pointer-events:none; transition:opacity .18s ease; }
.rm-flow-open .rm-brand { opacity:.5; transition:opacity .18s ease; }
.rm-flow-open .rm-stage { filter:saturate(.72) brightness(.78); transition:filter .2s ease; }
.rm-online-panel { margin-top:18px; padding-top:16px; border-top:1px solid var(--hair); }
.rm-online-heading { color:var(--ink-3); font-family:var(--fm); font-size:10px; font-weight:700; letter-spacing:.18em; margin-bottom:10px; }
.rm-online-list { display:grid; gap:6px; max-height:180px; overflow-y:auto; }
.rm-online-empty { color:var(--ink-3); font-size:11px; letter-spacing:.04em; padding:8px 2px; }
.rm-online-row { display:flex; align-items:center; justify-content:space-between; gap:12px; padding:8px 12px;
  border:1px solid var(--hair); background:rgba(8,14,19,.6); }
.rm-online-name { color:#fff; font-family:var(--fm); font-size:11px; letter-spacing:.05em; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.rm-online-duel { appearance:none; cursor:pointer; flex:none; border:1px solid rgba(111,208,255,.45); background:rgba(12,26,35,.78); color:var(--cyan);
  padding:6px 12px; font-family:var(--ff); font-size:9px; font-weight:800; letter-spacing:.14em; }
.rm-online-duel:hover:not(:disabled),.rm-online-duel:focus-visible { outline:0; background:rgba(38,106,135,.42); color:#fff; border-color:var(--cyan); }
.rm-online-duel:disabled { cursor:default; opacity:.55; color:var(--ink-2); border-color:var(--hair); }
.rm-challenge-maps { display:flex; flex-wrap:wrap; gap:8px; margin:14px 0; }
.rm-challenge-map-btn { appearance:none; cursor:pointer; border:1px solid var(--hair); background:rgba(4,9,13,.55); color:var(--ink-2);
  padding:8px 12px; font-family:var(--ff); font-size:9px; font-weight:700; letter-spacing:.11em; }
.rm-challenge-map-btn:hover,.rm-challenge-map-btn:focus-visible { outline:0; border-color:rgba(111,208,255,.55); color:#fff; }
.rm-challenge-map-btn.rm-challenge-map-active { border-color:rgba(111,208,255,.65); background:rgba(34,95,124,.34); color:#fff; }
.rm-challenge-actions { display:grid; grid-template-columns:1fr 1fr; gap:8px; margin-top:16px; }
.rm-queue-readout { position:relative; min-height:164px; overflow:hidden; display:flex; flex-direction:column; justify-content:center;
  gap:8px; padding:22px 24px; border:1px solid rgba(111,208,255,.38); border-left:3px solid var(--cyan);
  background:linear-gradient(115deg,rgba(15,37,49,.92),rgba(8,14,20,.9)); }
.rm-queue-readout::before { content:''; position:absolute; inset:0; opacity:.55; pointer-events:none;
  background:repeating-linear-gradient(90deg,transparent 0 18px,rgba(111,208,255,.05) 19px 20px); }
.rm-queue-status,.rm-queue-count,.rm-queue-detail { position:relative; z-index:1; }
.rm-queue-status { color:var(--cyan); font-family:var(--fd); font-size:16px; letter-spacing:.2em; }
.rm-queue-count { color:#fff; font-family:var(--fd); font-size:clamp(30px,3vw,42px); letter-spacing:.06em; line-height:1; }
.rm-queue-detail { color:var(--ink-2); font-family:var(--fm); font-size:10px; letter-spacing:.12em; }
.rm-queue-error .rm-queue-readout { border-color:rgba(255,94,94,.5); border-left-color:var(--red); background:linear-gradient(115deg,rgba(53,17,20,.92),rgba(18,8,10,.9)); }
.rm-queue-error .rm-queue-status { color:var(--red); }

/* ---------- account identity ----------------------------------------- */
.rm-account-trigger { appearance:none; cursor:pointer; align-self:center; margin-left:10px; border:1px solid rgba(111,208,255,.5); background:rgba(12,26,35,.78); color:var(--cyan); padding:7px 12px;
  font-family:var(--ff); font-size:9px; font-weight:800; letter-spacing:.16em; white-space:nowrap; transition:all .15s ease;
  text-shadow: 0 0 6px rgba(121,210,255,.4); }
.rm-account-trigger:hover,.rm-account-trigger:focus-visible { outline:0; background:rgba(38,106,135,.5); color:#fff; border-color:var(--cyan);
  box-shadow: 0 0 14px rgba(121,210,255,.3); }
.rm-account-modal { position:fixed; z-index:90; inset:0; display:grid; place-items:center; padding:22px; background:rgba(3,7,11,.75); backdrop-filter:blur(8px); }
.rm-account-modal[hidden] { display:none !important; }
.rm-account-card { width:min(440px,100%); position:relative; padding:28px; border:1px solid rgba(111,208,255,.45); border-left:3px solid var(--cyan);
  background:linear-gradient(135deg,rgba(18,30,40,.99),rgba(7,12,18,.99)); box-shadow:0 24px 80px rgba(0,0,0,.7), 0 0 40px rgba(111,208,255,.1);
  position:relative; overflow:hidden; }
.rm-account-card::before { content:''; position:absolute; left:0; right:0; top:0; height:1px;
  background: linear-gradient(90deg, transparent, var(--cyan), transparent); }
.rm-account-head { display:flex; align-items:flex-start; justify-content:space-between; gap:16px; padding-bottom:16px; border-bottom:1px solid var(--hair); }
.rm-account-kicker { display:block; color:var(--cyan); font-family:var(--fm); font-size:9px; letter-spacing:.24em;
  text-shadow: 0 0 8px rgba(121,210,255,.4); }
.rm-account-title { margin:5px 0 0; color:#fff; font-family:var(--fd); font-size:32px; font-weight:700; letter-spacing:.08em; line-height:1;
  background: linear-gradient(180deg, #fff 0%, #c8d8e6 100%);
  -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; }
.rm-account-close { appearance:none; cursor:pointer; border:0; background:transparent; color:var(--ink-2); font-family:var(--ff); font-size:9px; letter-spacing:.14em; text-transform:uppercase; font-weight:800;
  padding:6px 0; transition:all .15s ease; }
.rm-account-close:hover,.rm-account-close:focus-visible { color:#fff; outline:0; }
.rm-account-tabs { display:flex; gap:6px; margin:20px 0 16px; }
.rm-account-tab { appearance:none; cursor:pointer; flex:1; border:1px solid var(--hair); background:rgba(4,9,13,.55); color:var(--ink-2); padding:9px; font-family:var(--ff); font-size:9px; font-weight:800; letter-spacing:.12em; transition:all .15s ease; }
.rm-account-tab:hover { color:#fff; border-color: rgba(111,208,255,.4); }
.rm-account-tab.rm-on { border-color:rgba(111,208,255,.7); background:linear-gradient(180deg, rgba(34,95,124,.42), rgba(34,95,124,.18)); color:#fff;
  box-shadow: 0 0 14px rgba(121,210,255,.25), inset 0 1px 0 rgba(121,210,255,.3); }
.rm-account-form { display:grid; gap:12px; }
.rm-account-label { display:grid; gap:6px; color:var(--ink-2); font-family:var(--fm); font-size:9px; letter-spacing:.16em; }
.rm-account-input { min-width:0; box-sizing:border-box; width:100%; border:1px solid var(--hair); border-bottom-color:rgba(111,208,255,.55); background:rgba(2,7,11,.66); color:#fff; padding:12px 14px; font-family:var(--fm); font-size:13px; letter-spacing:.06em;
  transition: all .15s ease; }
.rm-account-input::placeholder { color:rgba(159,181,196,.44); }
.rm-account-input:focus { outline:0; border-color:var(--cyan); box-shadow:0 0 0 2px rgba(111,208,255,.18), inset 0 0 8px rgba(111,208,255,.1); }
.rm-account-submit { appearance:none; cursor:pointer; border:0; background:linear-gradient(180deg, var(--cyan-2), var(--cyan)); color:#071018; padding:13px; font-family:var(--ff); font-size:11px; font-weight:800; letter-spacing:.18em; transition: all .15s ease;
  box-shadow: 0 4px 12px rgba(121,210,255,.3), inset 0 1px 0 rgba(255,255,255,.3); }
.rm-account-submit:hover:not(:disabled),.rm-account-submit:focus-visible { outline:0; background:linear-gradient(180deg, #d7f4ff, var(--cyan-2));
  box-shadow: 0 6px 20px rgba(121,210,255,.5), inset 0 1px 0 rgba(255,255,255,.4); transform: translateY(-1px); }
.rm-account-submit:disabled { cursor:wait; opacity:.56; }
.rm-account-divider { display:flex; align-items:center; gap:10px; margin:18px 0 12px; color:var(--ink-3); font-family:var(--fm); font-size:8px; letter-spacing:.16em; }
.rm-account-divider::before,.rm-account-divider::after { content:''; flex:1; height:1px; background:var(--hair); }
.rm-account-providers { display:grid; grid-template-columns:1fr 1fr; gap:8px; }
.rm-account-provider { appearance:none; cursor:pointer; border:1px solid var(--hair); background:rgba(4,9,13,.55); color:#fff; padding:11px 8px; font-family:var(--ff); font-size:9px; font-weight:800; letter-spacing:.14em; transition: all .15s ease; }
.rm-account-provider:hover:not(:disabled),.rm-account-provider:focus-visible { outline:0; border-color:var(--cyan); background:rgba(34,95,124,.34);
  box-shadow: 0 0 12px rgba(121,210,255,.2); }
.rm-account-provider:disabled { cursor:not-allowed; color:var(--ink-3); opacity:.62; }
.rm-discord { color:#c5ccff; }
.rm-account-copy { margin:20px 0 14px; color:var(--ink-2); font-size:12px; line-height:1.55; }
.rm-account-message { min-height:17px; margin:14px 0 0; color:var(--amber); font-family:var(--fm); font-size:9px; letter-spacing:.06em; line-height:1.35;
  text-shadow: 0 0 6px rgba(255,176,42,.4); }
.rm-account-open .rm-stage { filter:saturate(.65) brightness(.68); }
.rm-challenge-open .rm-stage { filter:saturate(.65) brightness(.68); }

/* ---------- special popup --------------------------------------------- */
.rm-special-modal { position:fixed; z-index:95; inset:0; display:grid; place-items:center; padding:22px; background:rgba(3,7,11,.75); backdrop-filter:blur(8px); }
.rm-special-modal[hidden] { display:none !important; }
.rm-special-card { width:min(380px,100%); position:relative; padding:28px; border:1px solid rgba(111,208,255,.45); border-left:3px solid var(--cyan);
  background:linear-gradient(135deg,rgba(18,30,40,.99),rgba(7,12,18,.99)); box-shadow:0 24px 80px rgba(0,0,0,.7), 0 0 40px rgba(111,208,255,.1); text-align:center;
  position:relative; overflow:hidden; }
.rm-special-card::before { content:''; position:absolute; left:0; right:0; top:0; height:1px;
  background: linear-gradient(90deg, transparent, var(--cyan), transparent); }
.rm-special-copy { margin:0 0 20px; color:#fff; font-size:16px; line-height:1.55; white-space:pre-wrap; }
.rm-special-close { appearance:none; cursor:pointer; border:0; background:linear-gradient(180deg, var(--cyan-2), var(--cyan)); color:#071018; padding:11px 22px; font-family:var(--ff); font-size:10px; font-weight:800; letter-spacing:.18em; transition: all .15s ease;
  box-shadow: 0 4px 12px rgba(121,210,255,.3), inset 0 1px 0 rgba(255,255,255,.3); }
.rm-special-close:hover,.rm-special-close:focus-visible { outline:0; background:linear-gradient(180deg, #d7f4ff, var(--cyan-2));
  box-shadow: 0 6px 20px rgba(121,210,255,.5), inset 0 1px 0 rgba(255,255,255,.4); transform: translateY(-1px); }

/* ---------- minimal fallback ----------------------------------------- */
.rm-fallback { display:flex; align-items:center; justify-content:center; background:rgba(4,6,9,.9); }
.rm-fb-box { text-align:center; }
.rm-fb-title { font-family:var(--fd); font-size:80px; letter-spacing:.1em; color:#fff; margin-bottom:24px; }
.rm-fb-play { appearance:none; cursor:pointer; border:0; background:var(--amber); color:#100b02;
  font-family:var(--ff); font-weight:700; text-transform:uppercase; letter-spacing:.24em;
  font-size:16px; padding:16px 60px; clip-path:var(--shear); -webkit-clip-path:var(--shear); }
.rm-fb-play:hover { background:var(--amber-2); }

/* ---------- responsive: keep everything on screen -------------------- */
@media (max-width: 1100px) {
  .rm-stage { grid-template-columns: 1fr 1fr; grid-template-rows:auto auto minmax(0, 1fr) auto;
    grid-template-areas: "top top" "left right" "hero hero" "bot bot"; }
  .rm-hero { min-height:32vh; }
}

/* A large share of desktop and laptop displays are 1366x768 or 1440x900.
   The original screen only adapted to width, which left the lower parts of the
   right rail below the viewport on those displays. Keep the home screen a
   single-screen experience by using a deliberately compact desktop composition
   whenever vertical space is limited. The navigation, primary PLAY action, and
   deployment readout remain present; the supplementary operator file yields
   first because it is also available from the Operators navigation item. */
@media (min-width: 721px) and (max-height: 850px) {
  .rm-stage {
    grid-template-columns:minmax(250px, 25vw) 1fr minmax(210px, 21vw);
    grid-template-rows:auto minmax(0, 1fr) auto;
    grid-template-areas:
      "top top top"
      "left hero right"
      "bot bot bot";
    gap:clamp(8px, 1.1vh, 12px);
    padding:clamp(16px, 2.5vh, 24px) clamp(24px, 3.2vw, 44px);
  }
  .rm-top { gap:14px; }
  .rm-record { min-width:0; padding:8px 13px; }
  .rm-rank { width:36px; height:36px; }
  .rm-squadtop { display:none; }
  .rm-telemetry { gap:9px; }
  .rm-tel-cell { padding:7px 9px; }
  .rm-left { gap:clamp(8px, 1.3vh, 12px); }
  .rm-logo-main { font-size:clamp(42px, 5.5vw, 68px); }
  .rm-tagline { margin-top:4px; font-size:9px; }
  .rm-brand-meta { margin-top:7px; padding-top:7px; font-size:8px; }
  .rm-navitem { padding:clamp(6px, .9vh, 9px) 12px; }
  .rm-nav-label { font-size:clamp(14px, 1.45vw, 19px); }
  .rm-hint { padding:5px 0; margin-top:0; }
  .rm-dial { width:min(42vw, 46vh, 420px); }
  .rm-dial-bezel { width:min(50vw, 56vh, 500px); }
  .rm-right { gap:8px; justify-content:center; }
  .rm-readout { display:none; }
  .rm-panel { padding:10px 12px; }
  .rm-panel-title { font-size:14px; }
  .rm-deploy .rm-map { max-height:clamp(145px, 24vh, 190px); }
  .rm-map-legend { margin-top:5px; gap:8px; font-size:7.5px; }
  .rm-war { padding:9px 12px; }
  .rm-war-rows { gap:2px; margin-bottom:5px; }
  .rm-war-row { padding:3px 0; font-size:8px; }
  .rm-war-live { display:none; }
  .rm-war-bar { padding:4px 6px; }
  .rm-status-cell { padding:7px 12px; }
  .rm-status-cell .rm-mono { font-size:9px; }
  .rm-ticker-i { font-size:8px; }
}

@media (min-width: 721px) and (max-width: 1000px) and (max-height: 850px) {
  .rm-stage {
    grid-template-columns:minmax(230px, 34vw) 1fr;
    grid-template-areas:
      "top top"
      "left hero"
      "bot bot";
  }
  .rm-right { display:none; }
}

/* 720px-tall office laptops need a little more headroom still. The deployment
   card is the useful at-a-glance panel, so the live-intel duplicate is tucked
   away at this size rather than clipping it below the fold. */
@media (min-width: 721px) and (max-height: 740px) {
  .rm-war { display:none; }
  .rm-deploy .rm-map { max-height:clamp(130px, 22vh, 160px); }
  .rm-brand-meta { display:none; }
  .rm-bottom .rm-status-cell:not(.rm-news) { display:none; }
}
@media (max-width: 720px) {
  .rm-stage { grid-template-columns:1fr; grid-template-areas:"top" "left" "hero" "right" "bot"; overflow:auto; }
  .rm-right { display:none; }
  .rm-flow { left:22px; right:22px; top:48%; width:auto; }
  .rm-flow::before { inset:-20px -22px; background:rgba(6,10,14,.94); }
  .rm-flow-choice { grid-template-columns:minmax(92px,auto) minmax(0,1fr) 14px; gap:10px; padding:12px; }
  .rm-flow-choice-meta { display:none; }
  .rm-flow-title { font-size:29px; }
}
@media (prefers-reduced-motion: reduce) {
  .rm-root *, .rm-root *::before, .rm-root *::after { animation-duration:.001s !important; animation-iteration-count:1 !important; }
  .rm-scan, .rm-grain, .rm-motes, .rm-ring { display:none; }
}

/* ---- map load veil: shown while the page reloads onto the voted map ---- */
.rm-mapload { position:fixed; inset:0; z-index:80; display:flex; flex-direction:column;
  align-items:center; justify-content:center; gap:14px; background:#05070a;
  opacity:0; transition:opacity .18s ease; }
.rm-mapload-on { opacity:1; }
.rm-mapload-eyebrow { font-family:var(--ff,ui-monospace,monospace); font-size:10px; font-weight:700;
  letter-spacing:.34em; color:#6f8fa3; }
.rm-mapload-name { font-family:var(--ff,ui-monospace,monospace); font-size:clamp(26px,5vw,58px);
  font-weight:700; letter-spacing:.12em; color:#eaf3f8; }
.rm-mapload-bar { width:min(320px,52vw); height:2px; background:rgba(140,175,195,.16); overflow:hidden; }
.rm-mapload-bar > i { display:block; width:34%; height:100%; background:#e0a33a;
  animation:rm-mapload-sweep 1.1s ease-in-out infinite; }
@keyframes rm-mapload-sweep { 0%{transform:translateX(-110%)} 100%{transform:translateX(320%)} }
.rm-mapload-note { font-family:var(--ff,ui-monospace,monospace); font-size:9px; font-weight:700;
  letter-spacing:.2em; color:#54697a; }
@media (prefers-reduced-motion:reduce){ .rm-mapload-bar > i { animation:none; width:100%; } }
`;

export default MainMenu;
